import type { SyncScheduleDto } from '../utils/syncSchedule';
import type { IntegrationCapacity } from '../components/integrations/integrationLimitModel';
import { getAuthHeaders, makeApiCall, getApiUrl, readJsonResponse } from '../utils/apiConfig';

/** One Shopify price ledger row (MOT-500), as `GET /integrations/:id/price-ledger/runs/:runId` serves it. */
export interface PriceLedgerEntryDto {
  runId: string;
  productId: string;
  productName: string | null;
  productSku: string | null;
  shopifyProductId: string | null;
  variantId: string;
  field: 'price' | 'compareAtPrice';
  oldValue: number | null;
  newValue: number | null;
  source: string | null;
  outcome: string;
  writer: 'full_update' | 'pricing_only' | 'link_only' | 'activation';
  at: string;
}

export type ShopifyScopeGroup = 'inventory' | 'orders' | 'discounts' | 'markets';
export type ShopifySettingKey =
  | 'syncProducts'
  | 'syncPricing'
  | 'syncImages'
  | 'syncInventory'
  | 'orderSync'
  | 'writeDiscounts';

export interface ShopifySettingLock {
  locked: boolean;
  missing: string[];
  group: ShopifyScopeGroup | 'core';
}

export interface ShopifyScopeStatus {
  /** Scopes the store granted; null when the install predates scope recording */
  granted: string[] | null;
  missing: string[];
  /** true = reconnect needed for catalog access; null = unknown until checked */
  needsReauth: boolean | null;
  checkedAt?: string | null;
  requirements: Array<{ scope: string; reason: string }>;
  settingLocks?: Partial<Record<ShopifySettingKey, ShopifySettingLock>>;
  /** MOT-601: the grant lacks write_content, so a listing merge cannot redirect old URLs; null when unknown. */
  redirectsMissing?: boolean | null;
}

export interface IntegrationConfig {
  id: string;
  userId: string;
  platform: IntegrationPlatform;
  name: string;
  isActive: boolean;
  credentials: Record<string, any>;
  settings: IntegrationSettings;
  /** Plan features the store's plan does not include, so a switch can say so up front instead of failing on save. */
  planLocks?: { syncInventory?: boolean; customMetafieldMapping?: boolean };
  orderSyncEnabled?: boolean;
  lastSync?: string;
  createdAt: string;
  updatedAt: string;
  /** Shopify only: whether the grant still covers every scope Motzu asks for */
  scopeStatus?: ShopifyScopeStatus;
  /** Shopify only: whether the shop is on Plus, read from the shop at connect and on sync; null when never recorded. */
  isShopifyPlus?: boolean | null;
  /** Lightspeed only (`lightspeed_sales_sync`): high-water mark and last-run stats for the sales pull. */
  salesSync?: LightspeedSalesSyncState;
  /** Lightspeed only: set when a token expired mid-sync and the store must reconnect. */
  salesSyncAuthRequired?: boolean;
  /** Shopify only (`shopify_rebate_discounts`): result of the last rebate discount sync. */
  rebateDiscountSync?: ShopifyRebateDiscountSyncState;
  /** Shopify only (`bundle_shopify_discounts`): result of the last bundle rebate discount sync (same shape). */
  bundleDiscountSync?: ShopifyRebateDiscountSyncState;
  /** Lightspeed only (`lightspeed_store_config`): result of the last register-discount sync. */
  lightspeedDiscountSync?: LightspeedDiscountSyncState;
  /** Denormalized result of the last sync run; `skipReasons` / `warningCodes` are Lightspeed only (`lightspeed_sync_run_summary`). */
  lastSyncSummary?: {
    syncRunId?: string;
    status?: string;
    completedAt?: unknown;
    skipReasons?: Record<string, number>;
    warningCodes?: Record<string, number>;
    /** The latest run stopped on a problem only the store can fix (`lightspeed_ecom_write_forbidden`: a read-only API key); the card reads "Needs attention". */
    runErrorCode?: string;
    /** Lightspeed, flag `lightspeed_photo_limit`: products cut to the photo limit, and photos left out. */
    photoLimit?: { products: number; photosLeftOut: number };
  } | null;
  /** Post-connect settings walkthrough progress (flag `integration_setup_walkthrough`). */
  setupWalkthrough?: IntegrationSetupWalkthrough;
  /** Lightspeed eCom only (`lightspeed_ecom_connector`): where the resumable content push left off. */
  lightspeedEcomSync?: { nextOffset: number | null; updatedAt?: unknown } | null;
  /** BigCommerce only (`product_comparison`): Script Manager uuid of the installed storefront compare script. */
  compareScriptId?: string;
  /** BigCommerce only: Unix seconds the script's Storefront API token expires. */
  compareScriptTokenExpiresAt?: number;
  /** Flag `scheduled_sync_time` (MOT-476): the store's IANA zone the sync time is read in; absent with the flag off. */
  syncTimeZone?: string;
  /** When the scheduler will next run this integration (`resolveNextScheduledSync` on the API). */
  syncSchedule?: SyncScheduleDto | null;
}

/** Mirrors `integrations/{id}.setupWalkthrough`; the timestamps are Firestore values serialized as-is. */
export interface IntegrationSetupWalkthrough {
  version: 1;
  completedAt?: unknown;
  skippedAt?: unknown;
  completedSteps: string[];
}

/** The store's own choice per kind of Shopify rebate discount. A missing key means on. */
export interface RebateDiscountSettings {
  instant?: boolean;
  bundle?: boolean;
}

export interface SetupWalkthroughUpdate {
  completedSteps?: string[];
  completed?: boolean;
  skipped?: boolean;
}

/**
 * `lightspeed_sales_sync`: what the integration doc records about the last sales pull.
 * `lastSyncAt` is a Firestore server timestamp serialized as-is (like `lastSync` on
 * `IntegrationConfig`) — pass it through `formatLastSyncDate`, not a raw `Date` parse.
 */
export interface LightspeedSalesSyncState {
  lastSyncAt?: unknown;
  lastSyncCount?: number;
  lastSyncErrors?: number;
  /** High-water mark: sales completed after this are pulled on the next run — an ISO string. */
  lastCompleteTime?: string;
  lastSaleId?: string;
  /** Set alongside `salesSyncAuthRequired` when a pull failed to authorize. */
  lastError?: string;
}

/** Why a store's rebate is not delivered as a discount (rebates-only mode). */
export type RebatesOnlyHoldReason = 'below_floor' | 'shared_listing' | 'discount_limit' | 'price_review' | 'listing_not_read';

export interface RebatesOnlyStatus {
  discounted: number;
  held: Partial<Record<RebatesOnlyHoldReason, number>>;
  heldProducts: Array<{ productId: string; name: string; reason: RebatesOnlyHoldReason; livePrice: number | null; floor: number | null; amount: number }>;
  raised: number;
  raiseFailed: number;
  storeSetBelowMap: number;
  capSkipped: number;
  at?: unknown;
}

/** `GET /integrations/:id/rebates-only`. */
export interface RebatesOnlyState {
  enabled: boolean;
  unavailableReason: 'platform_paused' | 'no_discount_path' | null;
  status: RebatesOnlyStatus | null;
}

/** `GET /integrations/:id/rebates-only/preview`: what turning it on would do. */
export interface RebatesOnlyPreview {
  platform: 'shopify' | 'lightspeed';
  discounted: number;
  held: Partial<Record<RebatesOnlyHoldReason, number>>;
  heldProducts: Array<{ productId: string; name: string; reason: RebatesOnlyHoldReason; livePrice: number | null; amount: number }>;
  raises: { count: number; products: Array<{ productId: string; name: string; from: number; to: number }> };
  storeSetBelowMap: { count: number; products: Array<{ productId: string; name: string; livePrice: number; map: number }> };
  discountSlots: number | null;
  reconnectRequired: boolean;
}

/** An API failure that keeps its HTTP status, so a screen can tell a 403 from a broken read. */
export class RebatesOnlyApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/** What the integration doc records at `rebateDiscountSync`; also the shape of POST /integrations/:id/shopify/rebate-discounts/sync. */
export interface ShopifyRebateDiscountSyncState {
  at?: unknown;
  created: number;
  updated: number;
  skipped: number;
  deleted: number;
  capSkipped: number;
  errorCount?: number;
  errors?: string[];
  scopeStatus?: 'reconnect_required';
  missing?: string[];
}

/** `integrations/{id}.lightspeedDiscountSync`; also the shape of POST /integrations/:id/lightspeed/rebate-discounts/sync. */
export interface LightspeedDiscountSyncState {
  at?: unknown;
  created: number;
  updated: number;
  skipped: number;
  archived: number;
  errorCount?: number;
  errors?: string[];
  skippedReason?: 'disabled_by_store';
}

/** Lightspeed category mapping (flag `lightspeed_store_config`). */
export interface LightspeedLayoutSettings {
  categoryMap?: Record<string, string>;
  defaultCategoryId?: string | null;
  replaceCategories?: boolean;
  /** Flag `lightspeed_category_rules`: merchant registry entry id -> Lightspeed category id. */
  lightspeedCategoryByRegistryEntry?: Record<string, string>;
}

export interface LightspeedCustomFieldMapping {
  specLabel: string;
  customFieldId: string;
  name?: string;
  type?: string;
}

export interface LightspeedCategoryOption {
  categoryID: string;
  name: string;
  fullPathName: string;
  parentID: string | null;
}

export interface LightspeedCategoriesResponse {
  categories: LightspeedCategoryOption[];
  suggestions: Record<string, string>;
  slugs: string[];
  layout: LightspeedLayoutSettings;
  missingCategoryIds: string[];
  /** Flag `lightspeed_category_rules`: null when off. */
  categoryRules?: {
    registryEntries: Array<{ id: string; label: string }>;
    nameRules: Array<{ keywords: string[]; productType: string }>;
  } | null;
}

export interface LightspeedCustomFieldOption {
  customFieldID: string;
  name: string;
  type: string;
  writable: boolean;
}

export interface LightspeedSpecOption {
  label: string;
  productCount: number;
  sample: string;
  kind: 'text' | 'boolean' | 'number' | 'weight';
}

export interface LightspeedCustomFieldsResponse {
  fields: LightspeedCustomFieldOption[];
  specs: LightspeedSpecOption[];
  mappings: LightspeedCustomFieldMapping[];
}

export interface LightspeedProvisionResult {
  created: LightspeedCustomFieldMapping[];
  reused: LightspeedCustomFieldMapping[];
  failed: Array<{ specLabel: string; error: string }>;
  mappings: LightspeedCustomFieldMapping[];
}

export interface LightspeedCategoryChange {
  productId: string;
  productName: string | null;
  itemId: string;
  fromCategoryId: string;
  toCategoryId: string;
  reason: 'replaced' | 'forced';
  at: string | null;
}

/** Result of POST /integrations/:id/lightspeed/sales/sync and /integrations/:id/sync-orders. */
export interface OrderSyncRunResult {
  ordersProcessed: number;
  lineItemsSynced: number;
  lineItemsSkipped: number;
  errors: string[];
  lastOrderId?: string;
  lastOrderDate?: string;
}

/** Lightspeed eCom (C-Series), flag `lightspeed_ecom_connector`: the content fields a sync writes. */
export interface LightspeedEcomPushSettings {
  content?: boolean;
  description?: boolean;
  tags?: boolean;
  metafields?: boolean;
  visibility?: boolean;
}

export type LightspeedEcomCluster = 'us1' | 'eu1';

/** Which Lightspeed Retail integration's SKU crosswalk the eCom scan matched with (mirrors the API type). */
export interface LightspeedEcomRetailPairing {
  status: 'paired' | 'auto_paired' | 'choose' | 'none';
  retailIntegrationId: string | null;
  candidates: Array<{ id: string; name: string }>;
}

/** `integrationScans/{integrationId}` for a `lightspeed_ecom` integration (POST/GET /integrations/:id/lightspeed-ecom/scan). */
export interface LightspeedEcomScanReport {
  platform: 'lightspeed_ecom';
  scannedAt?: unknown;
  retailPairing?: LightspeedEcomRetailPairing;
  stats: {
    totalVariants: number;
    totalProducts: number;
    selectedProducts: number;
    matched: number;
    matchedByArticleCode: number;
    matchedByEan: number;
    alreadyLinked: number;
    unmatchedProducts: number;
    unmatchedItems: number;
  };
  matched: Array<{
    ecomProductId: string;
    ecomVariantId: string;
    ecomTitle: string;
    canonicalProductId: string;
    canonicalName: string;
    matchedBy: 'article_code' | 'ean';
    matchedIdentifier: string;
    canonicalBrand?: string;
  }>;
  unmatchedProducts: Array<{ canonicalProductId: string; canonicalName: string; sku?: string; upc?: string }>;
  unmatchedItems: Array<{ ecomProductId: string; ecomVariantId: string; title: string; articleCode?: string; ean?: string }>;
}

export type IntegrationPlatform = 
  | 'bigcommerce'
  | 'woocommerce'
  | 'lightspeed'
  | 'lightspeed_ecom'
  | 'shopify'
  | 'magento'
  | 'prestashop'
  | 'opencart';

export type SyncMode = 'link_only' | 'enrich' | 'override';

/** Listing fields Override mode may replace on a linked product. */
export type OverrideField = 'title' | 'description' | 'images' | 'tags' | 'vendorStatus' | 'productType' | 'weight';
/** Missing key = replace (default); false = keep the store's own value. */
export type OverrideFieldSettings = Partial<Record<OverrideField, boolean>>;

export interface StoreLayoutSettings {
  discoveredAt?: string;
  productTypesInUse?: string[];
  collections?: Array<{ id: string; title: string; handle: string; rules: any[] }>;
  menuStructure?: Array<{ title: string; url?: string; children: Array<{ title: string; url?: string; children: any[] }> }>;
  categoryMap?: Record<string, string>;
  /** Shopify standard category (taxonomy GID) per Motzu category slug. A row here beats Motzu's built-in default. */
  taxonomyCategoryMap?: Record<string, string>;
  /** Category for a product no row names. Missing: the neutral top-level "Cameras & Optics". */
  unmappedTaxonomyCategory?: string;
  /** Flag `shopify_mapping_simple_mode`. A Shopify category set on one product, keyed by Motzu product id. */
  productCategoryOverrides?: Record<string, string>;
  /** Flag `shopify_mapping_simple_mode`. The view the store last chose; default depends on whether it has a registry. */
  mappingView?: 'simple' | 'advanced';
  /** Shopify product_type for Motzu categories with no row in categoryMap (requires category/path on product). */
  defaultProductType?: string;
  allMotzuCategorySlugs?: string[];
  useDiscoveredMapping?: boolean;
  flagUnmappedCategories?: boolean;
  productTypeRules?: Array<{ keywords: string[]; productType: string; taxonomyCategoryGid?: string }>;
  history?: Array<{ savedAt: string; categoryMap: Record<string, string>; reason: string }>;
  /** Flag `shopify_field_ownership`. Who owns Shopify Category; missing means fill_blank. */
  shopifyCategoryOwner?: ShopifyFieldOwner;
  /** Flag `shopify_field_ownership`. Who owns Shopify Product Type; missing means fill_blank. */
  shopifyProductTypeOwner?: ShopifyFieldOwner;
}

export type ShopifyFieldOwner = 'motzu' | 'fill_blank' | 'never';

export interface StoreMetafieldDefinitionOption {
  namespace: string;
  key: string;
  name: string;
  type: string;
  /** False when the sync cannot fill this definition's type. */
  writable: boolean;
}

export interface MotzuMetafieldField {
  namespace: string;
  key: string;
  name: string;
  type: string;
  description: string;
  /** canonical: a standard Motzu spec. custom: the catch-all for vendor specs with no key of their own. */
  tier?: 'canonical' | 'custom';
  /** Canonical specifications path this field is written from (`lens.mount`); null when it has none. */
  specPath?: string | null;
  /** Pinned: shows on the product page in Shopify Admin. */
  pinned: boolean;
  group: string;
}

/** Live pins of Motzu's fields in Shopify Admin (flag `shopify_field_pinning`). */
export interface MotzuFieldPins {
  pinnedKeys: string[];
  missingKeys: string[];
  storePinnedCount: number;
  limit: number;
}

/** Where a Shopify store is in the `custom.*` -> `motzu.*` metafield move (flag `motzu_field_namespace`). */
export interface FieldNamespaceStatus {
  phase: 'legacy' | 'dual' | 'motzu';
  startedAt: string | null;
  completedAt: string | null;
  retiredAt: string | null;
  dualPassCompletedAt?: string | null;
  motzuDefinitions: { expected: number; missing: string[] };
  legacyDefinitions: { ownedByMotzu: number; ownedByStore: number };
}

export interface SpecCatalogEntry {
  label: string;
  productCount: number;
  sample: string;
  kind: 'text' | 'boolean' | 'number' | 'weight';
  /** Canonical specifications path (`lens.mount`), when the API sends it. */
  specPath?: string;
  /** Spec category, the section of the canonical sheet ("Storage", "Lens"); flat specs are "General". */
  category?: string;
  /** Lower-case phrases the catalog knows this spec by ("memory card type" for Slot Types); searched beside the label. */
  aliases?: string[];
}

export interface SpecCrosswalkIssue {
  /** Spreadsheet row, the header being row 1; 0 is a problem with the file itself. */
  row: number;
  message: string;
}

export interface SpecCrosswalkPreview {
  rows: Array<{ specLabel: string; namespace: string; key: string }>;
  errors: SpecCrosswalkIssue[];
  warnings: SpecCrosswalkIssue[];
  totalRows: number;
  unmapped: number;
  blank: number;
  maxMappings: number;
  /** The selection is larger than the crosswalk reads, so a label reported missing may be on an unread product. */
  catalogTruncated?: boolean;
}

export interface SpecMetafieldOptions {
  definitions: StoreMetafieldDefinitionOption[];
  definitionsError: string | null;
  specLabels: string[];
  /** Present once the API returns counts and samples. Older responses omit it. */
  specCatalog?: SpecCatalogEntry[];
  /** MOT-144 (flag `shopify_metafield_mapping`): the general mapping's choices; null with the flag off. */
  metafieldMapping?: MetafieldMappingOptions | null;
  /** MOT-641 (flag `spec_mapping_crosswalk`): the crosswalk download and upload; null with the flag off. */
  specCrosswalk?: { planLocked: boolean } | null;
  /** Synced products the counts are out of; null when the catalog could not be read, absent on older responses. */
  syncedProducts?: number | null;
  /** The selection held more products than the catalog read. */
  specCatalogTruncated?: boolean;
}

/** One Motzu field a store may map onto its own metafield (`src/config/metafieldSources.ts`). */
export interface MetafieldSource {
  id: string;
  label: string;
  group: 'identity' | 'pricing' | 'rebate' | 'launch' | 'compatibility' | 'spec';
  kind: 'text' | 'number' | 'date' | 'boolean' | 'json' | 'list';
  description: string;
  targetTypes: readonly string[];
  needsCompatibilityFlag?: boolean;
}

export interface MetafieldMappingTemplate {
  id: string;
  name: string;
  description: string;
  rows: Array<FieldMapping & { type: string }>;
}

export interface MetafieldMappingOptions {
  /** The plan lacks `customMetafieldMapping`: only the price-bearing sources render locked (MOT-574). */
  planLocked: boolean;
  sources: MetafieldSource[];
  templates: MetafieldMappingTemplate[];
}

export interface ShopifyDuplicateMappingRow {
  docId: string;
  platformProductId: string;
  isDeterministicDoc: boolean;
  lastSyncedAt: string | null;
  updatedAt: string | null;
}

export interface ShopifyDuplicateMappingGroup {
  productId: string;
  productName: string | null;
  deterministicDocId: string;
  recommendedKeepDocId: string;
  recommendedKeepShopifyId: string;
  recommendedRemoveDocIds: string[];
  rows: ShopifyDuplicateMappingRow[];
}

/** Unmapped Motzu-tagged listing sharing a title with a mapped product (failed update + recreate). */
export interface ShopifyOrphanSuspectRow {
  shopifyProductId: string;
  title: string;
  handle: string;
  status: string;
  imageCount: number;
  skus: string[];
  adminUrl: string;
}

export interface ShopifyOrphanSuspectGroup {
  normalizedTitle: string;
  mappedShopifyIds: string[];
  suspectedOrphans: ShopifyOrphanSuspectRow[];
  note: string;
}

export interface ShopifyOrphanSuspectsResponse {
  tag: string;
  motzuTaggedProductCount: number;
  mappedShopifyIdCount: number;
  groups: ShopifyOrphanSuspectGroup[];
}

/** State of a Shopify catalog scan job (`GET /integrations/:id/scan-products`). */
export type ShopifyScanJobStatus = 'none' | 'queued' | 'running' | 'done' | 'failed';
export interface ShopifyScanJob {
  status: ShopifyScanJobStatus;
  /** Identity of the run described; a finished result is adopted when this differs from the one shown. */
  runId?: string | null;
  progress: { pagesRead: number; productsSeen: number };
  error: string | null;
  requestedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  /** The preview of the finished scan; null until `done`. */
  result: ProductMatchResult | null;
}

export interface ProductMatchResult {
  matched: Array<{
    shopifyProductId: string;
    shopifyTitle: string;
    canonicalProductId: string;
    canonicalName: string;
    matchedBy: 'sku' | 'barcode';
    matchedIdentifier: string;
    /** False for a product of the store's brands that is not in its sync selection yet. Missing means true. */
    inSelection?: boolean;
    /** Set on read when a catalog gate (pro-only, refurbished, rental) excludes the product: Apply never adds it to the selection. */
    catalogGate?: { code: string; message: string };
  }>;
  unmatchedShopify: Array<{
    shopifyProductId: string;
    title: string;
    sku?: string;
    barcode?: string;
    vendor?: string;
  }>;
  unmatchedMotzu: Array<{
    canonicalProductId: string;
    name: string;
    sku?: string;
    upc?: string;
    brand?: string;
  }>;
  stats: {
    totalShopify: number;
    totalMotzu: number;
    matchedCount: number;
    matchRate: string;
    matchedOutsideSelection?: number;
    catalogTruncated?: boolean;
    /** Rows per list kept in the inline result; the full match list is paged from the scan report. */
    previewLimit?: number;
    /** Listings no Motzu product matched. The inline `unmatchedShopify` list is a preview of this many. */
    unmatchedShopifyCount?: number;
    /** Selected products no listing matched. The inline `unmatchedMotzu` list is a preview of this many. */
    unmatchedMotzuCount?: number;
  };
}

/** `lightspeed_link_only`: read-only scan report from POST/GET /integrations/:id/lightspeed/scan. */
export interface LightspeedScanReport {
  organizationId: string;
  integrationId: string;
  scannedAt: string;
  stats: {
    totalItems: number;
    /** Every match; the inline `matched` list below is a capped preview. */
    matched?: number;
    alreadyLinked?: number;
    matchedByUpc: number;
    matchedBySku: number;
    matchedByModel: number;
    unmatched: number;
    /** Selected Motzu products the scan did not find in Lightspeed. */
    unmatchedProducts?: number;
    /** Motzu products compared: the selection plus every product of the brands the store sells. */
    catalogProducts?: number;
    matchedInSelection?: number;
    matchedNotSelected?: number;
    /** The brand catalog hit the scan cap, so some products were not compared. */
    catalogTruncated?: boolean;
  };
  /** Chunk count per full row list; present on scans that kept every row (read them with `getLightspeedScanRows`). */
  rowChunks?: Record<LightspeedScanRowList, number>;
  matched: Array<{
    lightspeedItemId: string;
    lightspeedDescription: string;
    canonicalProductId: string;
    canonicalName: string;
    canonicalBrand?: string;
    inSelection?: boolean;
    matchedBy: 'upc' | 'sku' | 'model';
    matchedIdentifier: string;
  }>;
  /** Capped preview; `stats.unmatchedProducts` carries the true count. */
  unmatchedProducts?: Array<{
    canonicalProductId: string;
    canonicalName: string;
    upc?: string;
    sku?: string;
  }>;
  unmatchedItems: Array<{
    lightspeedItemId: string;
    description: string;
    upc?: string;
    sku?: string;
  }>;
}

export type LightspeedScanRowList = 'matched' | 'unmatched_items' | 'unmatched_products';
export type LightspeedScanMatchStatus = 'linked' | 'in_selection' | 'not_selected';

export interface LightspeedScanRowsQuery {
  list: LightspeedScanRowList;
  q?: string;
  status?: LightspeedScanMatchStatus;
  matchedBy?: 'upc' | 'sku' | 'model';
  brand?: string;
  offset?: number;
  limit?: number;
}

/** One row of any scan list; which fields are set depends on the list. */
export interface LightspeedScanRow {
  lightspeedItemId?: string;
  lightspeedDescription?: string;
  description?: string;
  canonicalProductId?: string;
  canonicalName?: string;
  canonicalBrand?: string;
  matchedBy?: 'upc' | 'sku' | 'model';
  matchedIdentifier?: string;
  upc?: string;
  sku?: string;
  /** `matched` only, computed when read. */
  status?: LightspeedScanMatchStatus;
  /** `matched` only: the catalog gate (pro-only, refurbished, rental) keeping this product from being linked, with the push's skip code. */
  catalogGate?: { code: string; message: string };
}

/** One match that was not linked, or was linked and then the Shopify update failed. */
export interface ApplyMatchSkip {
  canonicalProductId: string;
  shopifyProductId?: string;
  code: string;
  message: string;
  /** Set when another click can succeed. Absent on a catalog-gate skip. */
  retryable?: boolean;
  /** Shopify variant SKUs, when the listing belongs to a different product. */
  listingSkus?: string;
  evidence?: { sku: string; barcode: string; brand: string; title: string };
}

/**
 * Result of `POST /integrations/:id/apply-matches`.
 * `skips` names every match that was not linked. `nextIndex` is set when the server stopped
 * early to stay under the hosting time limit; the caller sends the rest.
 */
export interface ApplyMatchesResult {
  linked: number;
  enriched: number;
  overridden: number;
  errors: number;
  skipped: number;
  skips: ApplyMatchSkip[];
  updateFailures?: ApplyMatchSkip[];
  alreadyLinked?: number;
  deferred?: number;
  nextIndex?: number;
}

export interface LightspeedScanRowsPage {
  list: LightspeedScanRowList;
  rows: LightspeedScanRow[];
  total: number;
  offset: number;
  limit: number;
  summary?: Record<LightspeedScanMatchStatus, number>;
  brands?: string[];
}

export type DescriptionSection = 'prose' | 'features' | 'specs' | 'compatibility';

/** Shopify description layout (flag `description_templates`): sections render in this order; unlisted ones are omitted. */
export interface DescriptionTemplate {
  sections: DescriptionSection[];
  prose: 'full' | 'short';
  accordion: boolean;
}

export interface IntegrationSettings {
  /** Shopify and BigCommerce (flag `preorder_button`): pre-order state from launch dates, with the store's message (`{date}` = release date). */
  preorder?: { enabled: boolean; message?: string };
  /** Shopify, Lightspeed and BigCommerce, opt-in (flag `dealer_cost_sync`): dealer cost into the platform's merchant-only cost field. */
  dealerCostSync?: { enabled: boolean; owner?: 'fill_blank' | 'motzu' };
  /** BigCommerce (`motzu.compare_rows` metafield) and WooCommerce (`motzu_compare_rows` meta), opt-in (`product_comparison`): written on each sync. */
  compareRows?: boolean;
  /** Shopify and BigCommerce (flag `retailer_related_products`, MOT-705): where related and upsell picks go. */
  relatedProducts?: import('../types/relatedProducts').RelatedProductsSettings;
  /** BigCommerce (flag `bigcommerce_category_rules`): who owns the product `categories` field; missing means `motzu`. */
  bigcommerceCategoryOwner?: 'motzu' | 'fill_blank' | 'never';
  syncProducts: boolean;
  syncInventory: boolean;
  syncPricing: boolean;
  syncImages: boolean;
  /**
   * When true (default when omitted), exclude lifestyle/promotional/icon images from ERP pushes.
   * UI exposes the inverse as “Include marketing / lifestyle images”.
   */
  excludeMarketingImages?: boolean;
  addMotzuTag?: boolean;
  /** Lightspeed, flag `lightspeed_item_dimensions`: the account's unit system for item weight and size. Missing = imperial. */
  dimensionUnits?: 'imperial' | 'metric';
  /** Lightspeed, flag `lightspeed_photo_limit`: photos uploaded per product, 1 to 10. Missing = 3. */
  maxPhotos?: number;
  /** Lightspeed eCom (flag `lightspeed_ecom_connector`): which content fields the push writes. Missing = all but visibility. */
  lightspeedEcom?: LightspeedEcomPushSettings;
  /** Shopify, flag `shopify_tag_mode`. Missing = 'motzu' (today's behaviour). */
  tagMode?: 'motzu' | 'marker_only';
  defaultInventoryCount?: number;
  /** Shopify: maps to variant "Continue selling when out of stock" */
  continueSellingWhenOutOfStock?: boolean;
  allowProPublicSync?: boolean;
  /** Shopify: which rebate discounts Motzu keeps on the store. Missing key = on (when the admin flag is on). */
  rebateDiscounts?: RebateDiscountSettings;
  /** Set by API when allowProPublicSync is first turned on (audit). */
  allowProPublicSyncAcknowledgedAt?: string;
  allowProPublicSyncAcknowledgedBy?: string;
  autoSync: boolean;
  /** When true, new catalog products matching org brands are added to selection and synced automatically */
  autoSyncNewProducts?: boolean;
  /** When true, refurbished catalog products are pushed and auto-added. Off by default. */
  syncRefurbishedProducts?: boolean;
  /** When true, rental/loaner kits are pushed and auto-added. Off by default. */
  syncRentalProducts?: boolean;
  /**
   * Shopify: omitted/true groups kit/body siblings as variants on one listing.
   * Set false for a separate product per Motzu SKU.
   */
  groupKitBodyAsVariants?: boolean;
  /** Shopify, flag `shopify_listing_shape`. Missing = today's behaviour (variants, leave). */
  listingStructure?: 'individual' | 'variants';
  onUnsync?: 'leave' | 'draft' | 'archive';
  /** Store path (`/collections/cameras`) old product URLs redirect to when a listing is drafted or archived. */
  unsyncRedirectTo?: string;
  /** Shopify, flag `shopify_respect_store_deletes`: true creates a listing again after the store deleted it in Shopify. Missing = off. */
  recreateDeletedProducts?: boolean;
  /** Shopify / Lightspeed, flag `store_override_specs_push` (MOT-413): the store's own spec values win on its mapped spec fields. Missing = off. */
  pushStoreSpecs?: boolean;
  syncInterval: 'manual' | 'hourly' | 'daily' | 'weekly';
  /**
   * Flag `scheduled_sync_time` (MOT-476): local start time `HH:mm` in the store's time zone (hourly
   * uses the minute only). Missing or null = the interval rule. `syncWeekday` 0 (Sunday) to 6, weekly only.
   */
  syncTime?: string | null;
  syncWeekday?: number | null;
  syncMode?: SyncMode;
  /** Shopify, flag `initial_sync_mode`: what a selected product with no listing in the store gets. Missing = skip. */
  unmatchedProductAction?: 'create' | 'skip';
  /**
   * Lightspeed `link_only`: after linking, reprice items already linked.
   * Explicit true always reprices. With `lightspeed_catalog_parity`, an omitted value also reprices and explicit false opts out.
   */
  linkOnlyPricing?: boolean;
  /** Override mode only: which listing fields Motzu may replace. Omitted = all. */
  overrideFields?: OverrideFieldSettings;
  /** Shopify, flag `description_templates`. Missing = the fixed layout; null on save clears it. */
  descriptionTemplate?: DescriptionTemplate | null;
  fieldMappings: FieldMapping[];
  /** Shopify: spec label → a product metafield this store already defined. */
  specMetafields?: Array<{ specLabel: string; namespace: string; key: string }>;
  /** Shopify (`shopify_metafield_mapping`, MOT-144): any Motzu field (`sourceField`) → `namespace.key` the store defined. */
  metafieldMappings?: FieldMapping[];
  /** Shopify (`shopify_metafield_switches`): `false` turns one Motzu field off. Missing = on. */
  motzuMetafields?: Record<string, boolean>;
  /** Lightspeed (`lightspeed_store_config`): Motzu category → Lightspeed category. */
  lightspeedLayout?: LightspeedLayoutSettings;
  /** Lightspeed (`lightspeed_store_config`): catalog spec → item custom field. */
  lightspeedCustomFields?: LightspeedCustomFieldMapping[];
  filters?: IntegrationFilters;
  storeLayout?: StoreLayoutSettings;
  appliedStorefrontPreset?: AppliedStorefrontPreset;
  lastMatchResult?: ProductMatchResult;
}

export interface AppliedStorefrontPreset {
  presetId: string;
  presetName: string;
  version: string;
  dawnVersion: string;
  colors: {
    primary: string;
    accent?: string;
    neutral?: string;
  };
  appliedAt: string;
}

export interface StorefrontPresetSummary {
  id: string;
  name: string;
  description: string;
  screenshots: string[];
  versions: Array<{ version: string; dawnVersion: string }>;
}

export interface ThemeZipRequest {
  presetId: string;
  version?: string;
  primary: string;
  accent?: string;
  neutral?: string;
}

/** One shape for every mapping (MOT-144), mirroring `src/types/fieldMapping.ts`; `transform` is the old thin name the API still reads. */
export interface FieldMapping {
  sourceField: string;
  targetField: string;
  transformation?: 'none' | 'uppercase' | 'lowercase' | 'titlecase' | 'trim' | 'format_price' | 'format_date' | 'custom';
  /** @deprecated use `transformation` */
  transform?: 'uppercase' | 'lowercase' | 'trim' | 'format_price' | 'format_date';
  defaultValue?: string;
}

export interface IntegrationFilters {
  categories?: string[];
  brands?: string[];
  priceRange?: {
    min: number;
    max: number;
  };
  status?: 'active' | 'inactive' | 'draft';
}

export interface IntegrationTestResult {
  success: boolean;
  message: string;
  platform: IntegrationPlatform;
  connectionDetails?: {
    storeName?: string;
    apiVersion?: string;
    productCount?: number;
  };
  errors?: string[];
}

export interface IntegrationSyncResult {
  id: string;
  integrationId: string;
  status: 'queued' | 'running' | 'success' | 'error' | 'partial';
  productsProcessed: number;
  productsCreated: number;
  productsUpdated: number;
  productsSkipped: number;
  /** Grouped skip reasons (reason label → product count). Present on runs recorded after reason tracking shipped. */
  skipReasons?: Record<string, number>;
  errors: string[];
  errorCount?: number;
  warnings?: Array<string | { productId: string; message: string; code?: string; productName?: string; productSku?: string }>;
  warningCount?: number;
  startedAt: string;
  completedAt?: string;
  duration?: number;
  queued?: boolean;
  existing?: boolean;
}

export interface ShopifyInstallResponse {
  installUrl: string;
  callbackUrl?: string;
  state?: string;
  shop?: string;
  customApp?: boolean;
  /** A custom app Shopify has not installed yet: open `customAppInstallLink`, not `installUrl`. */
  needsInstallLink?: boolean;
  customAppInstallLink?: string | null;
}

/** A category of Shopify's full Standard Product Taxonomy, as `GET /integrations/shopify/taxonomy` serves it. */
export interface ShopifyTaxonomyNode {
  gid: string;
  /** Bare id, e.g. `co-2-7-4`. */
  id: string;
  name: string;
  /** Shopify's breadcrumb from Level 1, e.g. `Cameras & Optics > Cameras > Video Cameras > Camcorders`. */
  fullName: string;
  level: number;
  hasChildren: boolean;
}

/** A category choice: `label` is the short name tables show, `fullName` Shopify's breadcrumb when known. */
export interface ShopifyTaxonomyOption {
  gid: string;
  label: string;
  fullName?: string;
}

export interface ShopifyCategoryDefaults {
  neutralGid: string;
  options: ShopifyTaxonomyOption[];
  /** Motzu category slug to the taxonomy GID Motzu sends when the store maps nothing; null when none is built in. */
  defaults: Record<string, string | null>;
}

export type CategorySummarySource =
  | 'merchant_taxonomy'
  | 'product_override'
  | 'name_rule'
  | 'store_map'
  | 'default_map'
  | 'inferred'
  | 'store_fallback'
  | 'neutral';

/** Flag `shopify_mapping_simple_mode`: the Mapping tab's count of selected products by category source. */
export interface ShopifyCategorySummary {
  total: number;
  bySource: Record<CategorySummarySource, number>;
  /** Products with an explicit choice or a text inference (everything but the fallback and neutral). */
  confident: number;
  needsLook: Array<{
    productId: string;
    name: string;
    brand: string;
    storeSku: string;
    source: 'store_fallback' | 'neutral';
    suggestedLeafLabel?: string;
  }>;
  owners: { category: ShopifyFieldOwner; productType: ShopifyFieldOwner };
  integrationId: string;
  truncated: boolean;
}

/** Flag `structured_mapping_inputs`: a light row per product in the integration's sync selection. */
export interface MappingIndex {
  products: Array<{ id: string; name: string; model: string; brand: string; storeSku: string; categorySlugs: string[] }>;
  truncated: boolean;
  total: number;
}

export interface MerchantTaxonomyEntry {
  id: string;
  level: 'department' | 'category' | 'subcategory';
  department: string;
  departmentCode: string;
  category?: string;
  subcategory?: string;
  subcategoryCode?: string;
  skuPrefix?: string;
  shopifyCategoryGid?: string;
  productType?: string;
  motzuCategorySlugs?: string[];
}

export interface MerchantTaxonomyRegistry {
  organizationId: string;
  entries: MerchantTaxonomyEntry[];
  source: 'csv' | 'manual';
  importedAt?: string | null;
  updatedAt?: string;
  importedFileName?: string;
}

export interface MerchantTaxonomyStats {
  entries: number;
  leaves: number;
  mapped: number;
}

/** What importing a file would change, from `POST /store/merchant-taxonomy/import?dryRun=true`. */
export interface MerchantTaxonomyImportPreview {
  stats: MerchantTaxonomyStats;
  errors: Array<{ row: number; message: string }>;
  /** Rows read after a fix-up, e.g. a subcategory code that repeated its department code. */
  warnings?: Array<{ row: number; message: string }>;
  /** Stored subcategories the file leaves out; an import deletes them with their mappings. */
  wouldRemove: { leaves: number; mapped: number; ids: string[] };
  wouldAdd: number;
  /** Product assignments pointing at a removed subcategory; the import drops them. */
  wouldDropAssignments?: number;
}

/** A selected product whose store SKU matches no subcategory prefix and that has no assignment. */
export interface UnmatchedTaxonomyProduct {
  productId: string;
  name: string;
  brand: string;
  storeSku: string;
  motzuCategory: string;
}

export interface UnmatchedTaxonomyPage {
  items: UnmatchedTaxonomyProduct[];
  /** Position to pass as `cursor` for the next page; null when the selection is exhausted. */
  nextCursor: string | null;
  /** Products in the store's sync selection. */
  selected: number;
}

export interface TaxonomyAssignmentInput {
  productId: string;
  entryId: string;
  source: 'manual' | 'classifier';
  confidence?: number;
}

export interface ClassifierTreeNode {
  id: string;
  label: string;
  description?: string;
  children?: ClassifierTreeNode[];
}

export interface TaxonomyClassifierAnswer {
  questionId: string;
  optionId: string | null;
  confidence: number;
}

export type TaxonomyClassifierResult =
  | { productId: string; found: false }
  | {
    productId: string;
    found: true;
    name: string;
    brand: string;
    /** One answer per level descended; the last is where it stopped. */
    path: TaxonomyClassifierAnswer[];
    decided: boolean;
    stoppedAt: 'leaf' | 'low_confidence' | 'no_match';
  };

export interface MerchantTaxonomyPatch {
  id: string;
  shopifyCategoryGid?: string | null;
  productType?: string | null;
  skuPrefix?: string | null;
  motzuCategorySlugs?: string[] | null;
}

export interface ShopifyCustomAppScopes {
  scopes: string[];
  scopeString: string;
  core: string[];
  groups: Record<'inventory' | 'orders' | 'discounts' | 'markets', string[]>;
  /** MOT-601: the redirect scope a listing merge uses; listed in every custom app. */
  redirect?: string[];
  requirements: Array<{ scope: string; reason: string }>;
}

export interface ShopifyInstallUrls {
  appUrl: string;
  redirectUrl: string;
}

/** A store's own Shopify app saved in Motzu so its install can resume. The secret never comes back. */
/** Where a store stands in the custom app setup wizard. Never holds the client secret. */
export interface ShopifyCustomAppDraft {
  shopId: string;
  shopDomain: string;
  step: string;
  done: string[];
  clientId: string;
  installLink: string;
  updatedAt: string | null;
}

export interface ShopifyCustomAppSummary {
  id: string;
  shopId: string;
  shopDomain: string;
  clientId: string;
  status: 'awaiting_install' | 'installed' | 'failed';
  integrationId: string | null;
  lastError: { message: string; at: string | null } | null;
  /** The app's install link from Shopify's Distribution page, when saved. */
  installLink: string | null;
  /** Shopify has never opened Motzu for this app, so its first install must start from the install link. */
  needsInstallLink: boolean;
  savedAt: string | null;
  installedAt: string | null;
}

export interface LightspeedInstallResponse {
  installUrl: string;
  callbackUrl?: string;
  state?: string;
}

export type VendorConnectorType = 'api' | 'web_scraper';
export type VendorConnectorFrequency = 'manual' | 'daily' | 'weekly' | 'monthly' | 'custom';

export interface VendorConnectorSchedule {
  frequency: VendorConnectorFrequency;
  cron?: string;
  timezone?: string;
  isEnabled: boolean;
  lastRun?: string;
  nextRun?: string;
  lastRunStatus?: 'success' | 'error' | 'running' | 'idle';
}

export interface VendorIngestionConnectorDto {
  id: string;
  vendorId: string;
  vendorName: string;
  connectorName: string;
  connectorType: VendorConnectorType;
  handler?: {
    functionName?: string;
    runtime?: 'cloud_function' | 'http' | 'queue_worker';
    endpoint?: string;
  };
  dataDomains: string[];
  ingestionMode: 'full' | 'delta' | 'hybrid';
  supportsPagination: boolean;
  schedule: VendorConnectorSchedule;
  pagination?: {
    pageSize?: number;
    maxPages?: number;
    strategy?: 'offset' | 'cursor';
  };
  jobId?: string;
  integrationId?: string;
  status: 'active' | 'inactive' | 'error' | 'pending';
  errorCount: number;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

export interface RegisterVendorConnectorPayload {
  vendorId: string;
  vendorName: string;
  connectorName: string;
  connectorType: VendorConnectorType;
  handler?: VendorIngestionConnectorDto['handler'];
  dataDomains: string[];
  ingestionMode?: 'full' | 'delta' | 'hybrid';
  supportsPagination?: boolean;
  pagination?: {
    pageSize?: number;
    maxPages?: number;
    strategy?: 'offset' | 'cursor';
  };
  schedule?: Partial<VendorConnectorSchedule>;
  integrationId?: string;
  jobId?: string;
  metadata?: Record<string, any>;
}

export interface FastTrackRequestPayload {
  vendorName: string;
  notes?: string;
  priority?: 'normal' | 'high';
}

// Ingestion Job Types
export type IngestionJobStatus = 
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type IngestionJobType =
  | 'full_scrape'
  | 'incremental_scrape'
  | 'rescrape_missing'
  | 'api_ingestion'
  | 'image_hosting'
  | 'sync_canonical'
  | 'price_refresh'
  | 'stock_refresh'
  | 'market_pricing_refresh'
  | 'data_fill';

export interface PricingSubmission {
  id: string;
  fileName: string;
  vendor: string;
  region?: 'US' | 'CA';
  status: 'pending' | 'approved' | 'rejected' | 'processed' | 'duplicate';
  source?: 'upload' | 'email' | 'admin';
  recordsFound?: number;
  recordsCreated?: number;
  recordsUpdated?: number;
  createdAt?: string;
  processedAt?: string;
}

export interface ManagedBrandSummary {
  brand: string;
  fileCount: number;
  lastSubmittedAt: string | null;
}

export interface IngestionJobLog {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  data?: Record<string, any>;
}

export interface IngestionJobProgress {
  current: number;
  total: number;
  currentItem?: string;
  stage?: string;
}

export interface PricingReportChange {
  sku: string;
  name?: string;
  oldPrice: number;
  newPrice: number;
  changePercent: number;
  action: string;
  error?: string;
}

export interface PricingReportSummary {
  reportId?: string;
  totalChecked: number;
  noChange: number;
  fastTracked: number;
  /** Applied to the live product but flagged for admin review (large change or discontinuation). */
  appliedNeedsReview?: number;
  stagedForReview?: number;
  stagedUpdated: number;
  /** Failed checks. Not counted in `totalChanged`. */
  errored: number;
  totalChanged: number;
}

export interface PricingReport {
  id: string;
  jobId: string;
  brandId: string;
  createdAt: string;
  summary: PricingReportSummary;
  changes: PricingReportChange[];
  /** Failed checks (action `error`); not price changes. */
  failures?: PricingReportChange[];
}

export interface StalePricingProduct {
  productId: string;
  sku: string;
  name: string;
  brandId: string;
  brand?: string;
  lastCheckedAt: string | null;
  lastCheckedMs: number;
  staleDays: number | null;
  retail: number | null;
  failCount: number;
  missingUrl: boolean;
  sourceUrl: string;
  stagedProductId?: string;
  stagedStatus?: string;
}

export interface StalePricingReport {
  data: StalePricingProduct[];
  count: number;
  totalStale: number;
  truncated: boolean;
  summary: {
    staleAfterDays: number;
    scanned: number;
    stale: number;
    neverChecked: number;
    missingUrl: number;
    repeatedFailures: number;
    byBrand: Record<string, number>;
  };
}

export interface CatalogPriceChangeRow {
  reportId: string;
  /** Index of this change within the report's changes array — row identity for resolve. */
  changeIndex: number;
  jobId: string;
  brandId: string;
  region: string;
  reportedAt: string;
  sku: string;
  name: string;
  oldPrice: number;
  newPrice: number;
  changePercent: number;
  rawPriceChanges?: Array<{ field: 'msrp' | 'retail' | 'salePrice'; oldPrice: number; newPrice: number; changePercent: number; oldPriceIsFallback?: boolean }>;
  action: string;
  discontinued: boolean;
  suspicious: boolean;
  /** Which price moved: 'sale' rows carry the sale price in old/new. */
  field?: 'retail' | 'sale';
  /** Admin marked this row as reviewed; hidden from the report by default. */
  resolved?: boolean;
  productId?: string;
  /** Set when a deviation guard held this read for an admin. */
  priceMoveReview?: {
    reason: string;
    sku: string;
    sourceUrl?: string;
    matchedBy?: 'url' | 'image' | 'agent_sku';
    storedRetail?: number;
    storedList?: number;
    readRetail?: number;
    readList?: number;
    officialExactSku: boolean;
  };
}

export interface IngestionJobResult {
  productsProcessed: number;
  productsCreated: number;
  productsUpdated: number;
  productsFailed: number;
  imagesHosted: number;
  errors: string[];
  /** Set when the source site was down for maintenance and the run stopped. */
  sourceUnavailable?: string;
  pricingReport?: PricingReportSummary;
}

export interface IngestionJob {
  id: string;
  brandId: string;
  brandName: string;
  type: IngestionJobType;
  /** Cloud Run mode (`price_only`, `discovery`, `fill`, or omitted for full). */
  mode?: string;
  status: IngestionJobStatus;
  config: {
    limit?: number;
    category?: string;
    dryRun?: boolean;
    hostImages?: boolean;
    urls?: string[];
    region?: 'US' | 'CA';
  };
  progress?: IngestionJobProgress;
  result?: IngestionJobResult;
  recentLogs: IngestionJobLog[];
  /** Why a run that returned normally was recorded as failed (all price checks failed). */
  error?: string;
  /** Completed price refresh where more than half of the attempted checks failed. */
  partialFailure?: boolean;
  failedCount?: number;
  attemptedCount?: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  triggeredBy: string;
  triggeredByEmail?: string;
}

export interface TriggerIngestionPayload {
  type?: IngestionJobType;
  limit?: number;
  category?: string;
  hostImages?: boolean;
  urls?: string[];
  region?: 'US' | 'CA';
  mode?: 'full' | 'price_only';
  /** Admin force start: closes the brand's running job(s) as failed (audited), then starts. */
  force?: boolean;
  /** With force: the start (ISO) of the blocking run the admin confirmed; the server refuses if another run replaced it. */
  expectedRunningSince?: string | null;
}

export type ScheduleFrequency = 'manual' | 'daily' | 'weekly' | 'monthly';

export interface BrandScheduleConfig {
  brandId: string;
  frequency: ScheduleFrequency;
  isEnabled: boolean;
  /** Daily 4am UTC price refresh. Defaults to isEnabled when unset. */
  priceRefreshEnabled?: boolean;
  preferredTime?: string; // HH:MM in 24-hour format
  preferredDay?: number;  // 0-6 for weekly (Sunday=0), 1-28 for monthly
  timezone: string;
  nextScheduledRun?: string | null;
  lastScheduledRun?: string | null;
  updatedAt?: string;
  updatedBy?: string;
}

export interface UpdateSchedulePayload {
  frequency: ScheduleFrequency;
  isEnabled: boolean;
  priceRefreshEnabled?: boolean;
  preferredTime?: string;
  preferredDay?: number;
  timezone?: string;
}

export type IngestionSourcePlatform =
  | 'shopify'
  | 'woocommerce'
  | 'bigcommerce'
  | 'magento'
  | 'brand_api'
  | 'website';

/**
 * A standing site block (MOT-767): the brand's site refuses Chrome, so every
 * scheduled browser run is held and the daily price refresh probes at
 * `nextProbeAt`. Served by `GET /ingestion/brands` from
 * `brandScheduleConfigs.siteBlock`; absent or null while the site lets runs through.
 */
export interface IngestionSiteBlock {
  since: string;
  lastBlockedAt: string;
  lastJobId: string | null;
  host: string | null;
  probeCount: number;
  nextProbeAt: string;
  probeDue: boolean;
}

export interface IngestionBrandInfo {
  id: string;
  name: string;
  scrapeUrl: string | null;
  categories: string[];
  supportsIngestion: boolean;
  siteBlock?: IngestionSiteBlock | null;
  scraperType?: 'puppeteer' | 'feed' | 'api';
  apiConnectorId?: string;
  /** Platform the catalog is read from (mirrors `src/config/ingestionSourcePlatforms.ts`). */
  sourcePlatform?: IngestionSourcePlatform | null;
  cloudRunEnabled?: boolean;
  scraperStatus?: 'active' | 'ready' | 'coming_soon';
  schedule?: {
    frequency: ScheduleFrequency;
    isEnabled: boolean;
    priceRefreshEnabled?: boolean;
    preferredTime?: string;
    preferredDay?: number;
    timezone?: string;
    nextScheduledRun?: string | null;
  };
  lastRun?: {
    id: string;
    status: IngestionJobStatus;
    completedAt: string;
    startedAt?: string;
    durationMs?: number;
    durationFormatted?: string;
    executionMethod?: 'cloud_run' | 'local';
    productsProcessed: number;
    productsCreated: number;
    productsUpdated: number;
    productsFailed?: number;
    type?: IngestionJobType;
    mode?: string;
  } | null;
  stats?: {
    total: number;
    completed: number;
    failed: number;
    running: number;
    productCount: number;
  };
  recentRuns?: Array<{
    id: string;
    status: IngestionJobStatus;
    type?: IngestionJobType;
    createdAt: string | null;
    error?: string;
    productsProcessed?: number;
    productsFailed?: number;
    mode?: string;
  }>;
  lastError?: string | null;
  /** True only while a run younger than the server's live window says it is running. */
  isRunning?: boolean;
  /** Start (ISO) of the oldest live run; what Force start names. */
  runningSince?: string | null;
}

export interface TriggerIngestionResponse {
  jobId: string;
  message: string;
  status: IngestionJobStatus;
  executionMethod: 'cloud_run' | 'local';
  runCommand?: string;
  productsToProcess?: number;
}

export interface MetafieldProvisionSummary {
  created: number;
  alreadyExists: number;
  updated: number;
  typeConflicts: Array<{ key: string; existingType?: string }>;
  failed: Array<{ key: string; error?: string }>;
}

export interface ThemeBlockLink {
  handle: string;
  name: string;
  description: string;
  template: string;
  /** Deep link into the theme editor. Absent for a custom-app sections payload. */
  url?: string;
  preplace?: boolean;
  version?: number;
}

export interface CustomAppThemeSectionLinks {
  themeId: string;
  themeName: string;
  editorUrl: string;
  previewUrl: string;
  placed?: string[];
  unplaced?: string[];
}

export interface CustomAppThemeSectionResult extends CustomAppThemeSectionLinks {
  role?: string;
  duplicatedVia?: string;
  kept?: string[];
  metafieldWarning?: string;
  updateAvailable?: boolean;
}

export interface CustomAppThemeSummary {
  id: string;
  name: string;
  role: string;
  installed: boolean;
}

export interface CustomAppThemeSectionsInfo {
  scopesGranted: boolean | null;
  missingScopes: string[];
  updateAvailable: boolean;
  install: CustomAppThemeSectionLinks | null;
}

export interface ThemeBlocksResponse {
  blocks: ThemeBlockLink[];
  /** `not_configured` when Motzu's app has no extension id; `custom_app` when the store's own app cannot use that editor. */
  unavailableReason?: 'not_configured' | 'custom_app';
  requiresMetafieldDefinitions: boolean;
  requiresOnlineStore2Theme: boolean;
  mode?: 'custom_app' | 'custom_app_sections';
  message?: string;
  customAppSections?: CustomAppThemeSectionsInfo;
}

/** API error that keeps Shopify's `code` and `details` for the theme-sections card. */
export class IntegrationApiError extends Error {
  code?: string;
  details?: Record<string, unknown>;

  constructor(message: string, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'IntegrationApiError';
    if (code) this.code = code;
    if (details) this.details = details;
  }
}

function apiErrorMessage(data: any, fallback: string): string {
  if (typeof data?.error?.message === 'string' && data.error.message) return data.error.message;
  if (typeof data?.error === 'string' && data.error) return data.error;
  if (typeof data?.message === 'string' && data.message) return data.message;
  return fallback;
}

function integrationApiError(data: any, fallback: string): IntegrationApiError {
  const body = data?.error;
  const code = body && typeof body === 'object' && typeof body.code === 'string' ? body.code : undefined;
  const details = body && typeof body === 'object' && body.details && typeof body.details === 'object'
    ? body.details as Record<string, unknown>
    : undefined;
  return new IntegrationApiError(apiErrorMessage(data, fallback), code, details);
}

function lightspeedScanQueryString(query: Partial<LightspeedScanRowsQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  return params.toString();
}

class IntegrationApiService {
  // Use getApiUrl() to get the correct URL based on environment
  private get baseURL(): string {
    return getApiUrl();
  }

  private getHeaders = async () => {
    return await getAuthHeaders();
  }

  getIntegrations = async (): Promise<IntegrationConfig[]> => {
    const response = await makeApiCall('/integrations', {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    return data.data || [];
  }

  getIntegration = async (id: string): Promise<IntegrationConfig> => {
    const response = await makeApiCall(`/integrations/${id}`, {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    return data.data;
  }

  createIntegration = async (integration: Omit<IntegrationConfig, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<IntegrationConfig> => {
    const response = await makeApiCall('/integrations', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(integration),
    });
    return this.readData<IntegrationConfig>(response);
  }

  updateIntegration = async (id: string, updates: Partial<IntegrationConfig>): Promise<IntegrationConfig> => {
    const response = await makeApiCall(`/integrations/${id}`, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: JSON.stringify(updates),
    });
    return this.readData<IntegrationConfig>(response);
  }

  /** BigCommerce (`product_comparison`): register or refresh the storefront compare script. */
  installBigCommerceCompareScript = async (id: string): Promise<{ scriptId: string; tokenExpiresAt: number; updated: boolean }> => {
    const response = await makeApiCall(`/integrations/${id}/bigcommerce/compare-script`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({}),
    });
    return this.readData(response);
  }

  /** BigCommerce (`product_comparison`): remove the storefront compare script. */
  removeBigCommerceCompareScript = async (id: string): Promise<{ removed: boolean }> => {
    const response = await makeApiCall(`/integrations/${id}/bigcommerce/compare-script`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });
    return this.readData(response);
  }

  /** Record walkthrough progress; only the fields sent are written. */
  updateSetupWalkthrough = async (id: string, body: SetupWalkthroughUpdate): Promise<IntegrationSetupWalkthrough | null> => {
    const response = await makeApiCall(`/integrations/${id}/setup-walkthrough`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify(body),
    });
    const data = await this.readData<{ setupWalkthrough: IntegrationSetupWalkthrough | null }>(response);
    return data.setupWalkthrough;
  }

  /** Turn Shopify order sync on or off (doc-level `orderSyncEnabled`). */
  updateOrderSync = async (id: string, orderSyncEnabled: boolean): Promise<void> => {
    const response = await makeApiCall(`/integrations/${id}/order-sync`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify({ orderSyncEnabled }),
    });
    await this.readData<unknown>(response);
  }

  /** Swap a Shopify integration's Admin API token; the server tests it before saving. */
  reconnectShopifyToken = async (id: string, shop: string, accessToken: string): Promise<IntegrationConfig> => {
    const response = await makeApiCall(`/integrations/${id}/shopify/reconnect-token`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ shop, accessToken }),
    });
    return this.readData<IntegrationConfig>(response);
  }

  deleteIntegration = async (id: string): Promise<void> => {
    await makeApiCall(`/integrations/${id}`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });
  }

  /**
   * Tests a saved integration on the server with the credentials it stores. `credentials` (what the
   * store retyped in the edit form) is merged over them for this test only; a blank secret keeps the
   * stored one.
   */
  testIntegration = async (id: string, credentials?: Record<string, unknown>): Promise<IntegrationTestResult> => {
    const response = await makeApiCall(`/integrations/${id}/test`, {
      method: 'POST',
      headers: await this.getHeaders(),
      ...(credentials ? { body: JSON.stringify({ credentials }) } : {}),
    });
    return this.readData<IntegrationTestResult>(response);
  }

  /**
   * Lightspeed eCom: test a key, secret and cluster before connecting. Credential-only on the
   * server: no integration is created, so nothing is billed or reconciled.
   */
  testLightspeedEcomCredentials = async (credentials: Record<string, unknown>): Promise<IntegrationTestResult> => {
    const response = await makeApiCall('/integrations/lightspeed-ecom/test-credentials', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ credentials }),
    });
    return this.readData<IntegrationTestResult>(response);
  }

  /** Lightspeed eCom: choose which Lightspeed Retail integration wrote this shop's article codes. */
  setLightspeedEcomRetailPairing = async (integrationId: string, retailIntegrationId: string): Promise<LightspeedEcomRetailPairing> => {
    const response = await makeApiCall(`/integrations/${integrationId}/lightspeed-ecom/retail-pairing`, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: JSON.stringify({ retailIntegrationId }),
    });
    return this.readData<LightspeedEcomRetailPairing>(response);
  }

  /** Reject non-2xx responses with the server's message instead of returning undefined data. */
  private readData = async <T,>(response: Response): Promise<T> => {
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(body?.error?.message || body?.message || `Request failed (${response.status})`);
    }
    return body?.data as T;
  }

  syncIntegration = async (
    id: string,
    mode?: 'queued' | 'direct',
    options?: { forceFullSync?: boolean; syncType?: string; syncImages?: boolean },
  ): Promise<IntegrationSyncResult> => {
    const params = new URLSearchParams();
    if (mode) params.set('mode', mode);
    if (options?.forceFullSync) params.set('force', 'true');
    if (options?.syncType) params.set('syncType', options.syncType);
    const query = params.toString() ? `?${params.toString()}` : '';
    const headers = await this.getHeaders();
    const hasBody = typeof options?.syncImages === 'boolean';
    const response = await makeApiCall(`/integrations/${id}/sync${query}`, {
      method: 'POST',
      headers: hasBody ? { ...headers, 'Content-Type': 'application/json' } : headers,
      body: hasBody ? JSON.stringify({ syncImages: options!.syncImages }) : undefined,
    });
    const data = await readJsonResponse(response, 'Sync request failed');
    if (!response.ok && !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Sync request failed');
    }
    return data.data;
  }

  cancelIntegrationSync = async (integrationId: string, syncId: string): Promise<void> => {
    const response = await makeApiCall(`/integrations/${integrationId}/syncs/${syncId}/cancel`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || data?.error || 'Failed to cancel sync');
    }
  }

  refreshShopifyScopes = async (integrationId: string): Promise<ShopifyScopeStatus> => {
    const response = await makeApiCall(`/integrations/${integrationId}/shopify/refresh-scopes`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || !body?.success) {
      throw new Error(body?.message || body?.error?.message || 'Failed to check Shopify permissions');
    }
    return body.data as ShopifyScopeStatus;
  };

  getShopifyCustomApps = async (): Promise<ShopifyCustomAppSummary[]> => {
    const response = await makeApiCall('/shopify/custom-apps', {
      headers: await this.getHeaders(),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body?.message || body?.error?.message || 'Failed to load saved Shopify apps');
    }
    return (body?.data || []) as ShopifyCustomAppSummary[];
  };

  saveShopifyCustomApp = async (
    shop: string,
    clientId: string,
    clientSecret: string,
    installLink?: string,
  ): Promise<ShopifyCustomAppSummary> => {
    const response = await makeApiCall('/shopify/custom-apps', {
      method: 'PUT',
      headers: { ...(await this.getHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shop: shop.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        ...(installLink?.trim() ? { installLink: installLink.trim() } : {}),
      }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body?.message || body?.error?.message || 'Failed to save the Shopify app');
    }
    return body.data as ShopifyCustomAppSummary;
  };

  /** Add, change or clear (null) the install link of a saved app. */
  setShopifyCustomAppInstallLink = async (shopId: string, installLink: string | null): Promise<ShopifyCustomAppSummary> => {
    const response = await makeApiCall(`/shopify/custom-apps/${encodeURIComponent(shopId)}`, {
      method: 'PATCH',
      headers: { ...(await this.getHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({ installLink: installLink?.trim() || null }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body?.message || body?.error?.message || 'Failed to save the install link');
    }
    return body.data as ShopifyCustomAppSummary;
  };

  deleteShopifyCustomApp = async (shopId: string): Promise<void> => {
    const response = await makeApiCall(`/shopify/custom-apps/${encodeURIComponent(shopId)}`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.message || body?.error?.message || 'Failed to remove the saved Shopify app');
    }
  };

  getShopifyCustomAppDrafts = async (): Promise<ShopifyCustomAppDraft[]> => {
    const response = await makeApiCall('/shopify/custom-app-drafts', {
      headers: await this.getHeaders(),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body?.message || body?.error?.message || 'Failed to load Shopify setup progress');
    }
    return (body?.data || []) as ShopifyCustomAppDraft[];
  };

  saveShopifyCustomAppDraft = async (
    shopId: string,
    draft: Pick<ShopifyCustomAppDraft, 'step' | 'done' | 'clientId' | 'installLink'>,
  ): Promise<void> => {
    const response = await makeApiCall(`/shopify/custom-app-drafts/${encodeURIComponent(shopId)}`, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: JSON.stringify(draft),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.message || body?.error?.message || 'Failed to save Shopify setup progress');
    }
  };

  deleteShopifyCustomAppDraft = async (shopId: string): Promise<void> => {
    const response = await makeApiCall(`/shopify/custom-app-drafts/${encodeURIComponent(shopId)}`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.message || body?.error?.message || 'Failed to remove Shopify setup progress');
    }
  };

  getShopifyInstallUrls = async (): Promise<ShopifyInstallUrls> => {
    const response = await makeApiCall('/shopify/oauth/install-urls', {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || !data?.data?.appUrl || !data?.data?.redirectUrl) {
      throw new Error(data?.message || data?.error?.message || 'Failed to load Shopify app URLs');
    }
    return data.data as ShopifyInstallUrls;
  }

  startShopifyInstall = async (
    shopDomain: string,
    opts?: {
      customClientId?: string;
      customClientSecret?: string;
      /** Use the Client ID and secret saved for this shop instead of sending them again. */
      useSavedCustomApp?: boolean;
      /** The custom app's Shopify install link, saved with it for an owner/admin. */
      installLink?: string;
      scopeGroups?: ShopifyScopeGroup[];
      unmatchedProductAction?: 'create' | 'skip';
    }
  ): Promise<ShopifyInstallResponse> => {
    const trimmed = shopDomain.trim();
    if (!trimmed) {
      throw new Error('Shop domain is required');
    }
    const hasCustomCreds = !!(opts?.customClientId && opts?.customClientSecret);
    const useSavedCustomApp = !hasCustomCreds && !!opts?.useSavedCustomApp;
    const headers = await this.getHeaders();
    const scopeGroups = opts?.scopeGroups;
    const hasScopeGroups = Array.isArray(scopeGroups);

    if (hasCustomCreds || useSavedCustomApp || hasScopeGroups) {
      const response = await makeApiCall(`/shopify/oauth/start`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop: trimmed,
          ...(hasCustomCreds
            ? { customClientId: opts!.customClientId, customClientSecret: opts!.customClientSecret }
            : {}),
          ...(useSavedCustomApp ? { useSavedCustomApp: true } : {}),
          ...((hasCustomCreds || useSavedCustomApp) && opts?.installLink?.trim()
            ? { installLink: opts.installLink.trim() }
            : {}),
          ...(scopeGroups ? { scopeGroups } : {}),
          ...(opts?.unmatchedProductAction ? { unmatchedProductAction: opts.unmatchedProductAction } : {}),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || data?.error?.message || 'Failed to start Shopify install');
      }
      return data.data;
    }

    const response = await makeApiCall(`/shopify/oauth/start?shop=${encodeURIComponent(trimmed)}`, {
      headers,
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || data?.error?.message || 'Failed to start Shopify install');
    }
    return data.data;
  }

  /**
   * Plan allowance for another integration (MOT-808). `canAdd` is false when the
   * store is already at `maxIntegrations` and the additional-integration add-on
   * is not available. A failed read should not block the page: the start routes
   * still refuse.
   */
  getIntegrationCapacity = async (): Promise<IntegrationCapacity> => {
    const response = await makeApiCall('/integrations/capacity', {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || !data?.data) {
      throw new Error(data?.message || data?.error?.message || 'Failed to load integration capacity');
    }
    return data.data;
  }

  startLightspeedInstall = async (opts?: { reconnectIntegrationId?: string }): Promise<LightspeedInstallResponse> => {
    const reconnectId = opts?.reconnectIntegrationId?.trim();
    const query = reconnectId ? `?reconnectIntegrationId=${encodeURIComponent(reconnectId)}` : '';
    const response = await makeApiCall(`/lightspeed/oauth/start${query}`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || data?.error?.message || 'Failed to start Lightspeed connect');
    }
    return data.data;
  }

  getIntegrationSyncs = async (id: string, limit = 10, offset = 0): Promise<IntegrationSyncResult[]> => {
    const response = await makeApiCall(`/integrations/${id}/syncs?limit=${limit}&offset=${offset}`, {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    return data.data || [];
  }

  getIntegrationHistory = async (id: string, limit = 50): Promise<any> => {
    const response = await makeApiCall(`/integrations/${id}/history?limit=${limit}`, {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || data?.error?.message || `Failed to load integration history (${response.status})`);
    }
    return data.data || [];
  }

  /** One page of a sync run's Shopify price ledger (flag `shopify_price_ledger`, MOT-500). */
  getPriceLedgerRows = async (
    integrationId: string,
    runId: string,
    cursor?: string | null,
  ): Promise<{ entries: PriceLedgerEntryDto[]; nextCursor: string | null; truncatedProducts: number }> => {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const response = await makeApiCall(
      `/integrations/${encodeURIComponent(integrationId)}/price-ledger/runs/${encodeURIComponent(runId)}${query}`,
      { headers: await this.getHeaders() },
    );
    const data = await readJsonResponse(response, 'Could not load the price ledger');
    if (!response.ok) throw new Error(data?.message || data?.error?.message || `Could not load the price ledger (${response.status})`);
    return { entries: data.data || [], nextCursor: data.nextCursor ?? null, truncatedProducts: data.truncatedProducts ?? 0 };
  }

  /** Downloads a run's whole price ledger as CSV. */
  downloadPriceLedgerCsv = async (integrationId: string, runId: string): Promise<void> => {
    const response = await makeApiCall(
      `/integrations/${encodeURIComponent(integrationId)}/price-ledger/runs/${encodeURIComponent(runId)}/csv`,
      { headers: await this.getHeaders() },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data?.message || data?.error?.message || 'Could not download the price ledger');
    }
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `price-ledger-${runId.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 80) || 'run'}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(downloadUrl);
  }

  getVendorConnectors = async (): Promise<VendorIngestionConnectorDto[]> => {
    const response = await makeApiCall('/admin/integrations/data-ingestion/connectors', {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    return data.data || [];
  }

  registerVendorConnector = async (
    payload: RegisterVendorConnectorPayload
  ): Promise<VendorIngestionConnectorDto> => {
    const response = await makeApiCall('/system/data-ingestion/connectors/register', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    return data.data;
  }

  updateVendorConnector = async (
    id: string,
    updates: Partial<RegisterVendorConnectorPayload>
  ): Promise<VendorIngestionConnectorDto> => {
    const response = await makeApiCall(`/admin/integrations/data-ingestion/connectors/${id}`, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: JSON.stringify(updates)
    });
    const data = await response.json();
    return data.data;
  }

  runVendorConnector = async (
    id: string,
    executionType: 'manual' | 'scheduled' = 'manual',
    options: { force?: boolean; expectedRunningSince?: string | null } = {}
  ): Promise<{ message: string }> => {
    const response = await makeApiCall(`/admin/integrations/data-ingestion/connectors/${id}/run`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({
        executionType,
        ...(options.force
          ? { force: true, ...(options.expectedRunningSince ? { expectedRunningSince: options.expectedRunningSince } : {}) }
          : {})
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.message || data.error || 'Failed to queue API connector run');
    }
    return data;
  }

  createFastTrackRequest = async (payload: FastTrackRequestPayload): Promise<void> => {
    await makeApiCall('/admin/integrations/data-ingestion/fast-track', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(payload)
    });
  }

  // Ingestion Job Methods
  getIngestionJobs = async (brandId?: string, limit = 50): Promise<IngestionJob[]> => {
    const params = new URLSearchParams();
    if (brandId) params.append('brandId', brandId);
    params.append('limit', limit.toString());
    
    const response = await makeApiCall(`/ingestion/jobs?${params.toString()}`, {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    return data.data || [];
  }

  // Rebate/MAP files a vendor has submitted (pricingSubmissions), regardless
  // of which manufacturer brand each row applies to.
  getVendorPricingSubmissions = async (vendorName: string, limit = 25): Promise<PricingSubmission[]> => {
    if (!vendorName) return [];
    const params = new URLSearchParams();
    params.set('vendor', vendorName);
    params.set('limit', String(limit));
    const response = await makeApiCall(`/pricing-ingestion/submissions?${params.toString()}`, {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    return data.data || [];
  }

  // Update an API connector's ingestion frequency (e.g. weekly -> daily for
  // vendor-API-sourced brands like ProMaster).
  updateConnectorSchedule = async (
    connectorId: string,
    frequency: 'manual' | 'daily' | 'weekly' | 'monthly'
  ): Promise<void> => {
    const response = await makeApiCall(`/admin/integrations/data-ingestion/connectors/${connectorId}`, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: JSON.stringify({ schedule: { frequency, isEnabled: true } }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'Failed to update connector schedule');
    }
  }

  // Brands a distributor manages MAP/rebate pricing for, derived from their
  // submission history.
  getVendorManagedBrands = async (vendorName: string): Promise<ManagedBrandSummary[]> => {
    if (!vendorName) return [];
    const params = new URLSearchParams();
    params.set('vendor', vendorName);
    const response = await makeApiCall(`/pricing-ingestion/managed-brands?${params.toString()}`, {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    return data.data || [];
  }

  getRunningIngestionJobs = async (): Promise<IngestionJob[]> => {
    const response = await makeApiCall('/ingestion/jobs/running', {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    return data.data || [];
  }

  getIngestionJob = async (jobId: string): Promise<IngestionJob> => {
    const response = await makeApiCall(`/ingestion/jobs/${jobId}`, {
      headers: await this.getHeaders()
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.data) {
      throw new Error(data.error || 'Job not found');
    }
    return data.data;
  }

  getIngestionJobLogs = async (jobId: string, limit = 500): Promise<IngestionJobLog[]> => {
    const response = await makeApiCall(`/ingestion/jobs/${jobId}/logs?limit=${limit}`, {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    return data.data || [];
  }

  cancelIngestionJob = async (jobId: string): Promise<void> => {
    await makeApiCall(`/ingestion/jobs/${jobId}/cancel`, {
      method: 'POST',
      headers: await this.getHeaders()
    });
  }

  triggerSonyIngestion = async (payload: TriggerIngestionPayload = {}): Promise<TriggerIngestionResponse> => {
    return this.triggerIngestion('sony', payload);
  }

  triggerIngestion = async (brandId: string, payload: TriggerIngestionPayload = {}): Promise<TriggerIngestionResponse> => {
    const response = await makeApiCall(`/ingestion/trigger/${brandId}`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      const message = data?.error || data?.message || 'Failed to trigger ingestion job';
      const error: any = new Error(message);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data.data;
  }

  triggerPriceRefresh = async (brandId?: string, region?: 'US' | 'CA'): Promise<TriggerIngestionResponse> => {
    const response = await makeApiCall('/ingestion/trigger/price-refresh', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ brandId, region }),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      const message = data?.error || data?.message || 'Failed to trigger price refresh';
      const error: any = new Error(message);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data.data;
  }

  triggerMarketPricingRefresh = async (brandId?: string, limit = 50): Promise<{ processed: number; refreshed: number; skipped: number; failed: number; message: string }> => {
    const response = await makeApiCall('/ingestion/trigger/market-pricing', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ brandId, limit }),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      const message = data?.error || data?.message || 'Failed to trigger market pricing refresh';
      const error: any = new Error(message);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data.data;
  }

  triggerStockRefresh = async (brandId?: string, region?: 'US' | 'CA'): Promise<TriggerIngestionResponse> => {
    const response = await makeApiCall('/ingestion/trigger/stock-refresh', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ brandId, region }),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      const message = data?.error || data?.message || 'Failed to trigger stock refresh';
      const error: any = new Error(message);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data.data;
  }

  triggerRescrapeMissing = async (brandId = 'sony'): Promise<TriggerIngestionResponse> => {
    const response = await makeApiCall('/ingestion/trigger/rescrape-missing', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ brandId })
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      const message = data?.error || data?.message || 'Failed to trigger rescrape job';
      const error: any = new Error(message);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data.data;
  }

  // Store Layout Discovery
  discoverStoreLayout = async (integrationId: string): Promise<{ discovered: any; current: any }> => {
    const response = await makeApiCall(`/integrations/${integrationId}/discover-layout`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    return data.data;
  }

  applyStoreLayout = async (integrationId: string, payload: {
    categoryMap: Record<string, string>;
    collections?: any[];
    menuStructure?: any[];
    productTypesInUse?: string[];
    defaultProductType?: string;
    allMotzuCategorySlugs?: string[];
  }): Promise<StoreLayoutSettings> => {
    const response = await makeApiCall(`/integrations/${integrationId}/apply-layout`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return data.data;
  }

  patchStoreLayout = async (integrationId: string, categoryMapUpdates: Record<string, string>): Promise<{ categoryMap: Record<string, string> }> => {
    const response = await makeApiCall(`/integrations/${integrationId}/layout`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify({ categoryMap: categoryMapUpdates }),
    });
    const data = await response.json();
    return data.data;
  }

  /**
   * Choices for the Spec metafields editor (the shop's own definitions + the spec catalog of its synced
   * products). The catalog is cached 60 s on the server; `refresh` rebuilds it (the "Re-read" button).
   */
  getSpecMetafieldOptions = async (integrationId: string, options: { refresh?: boolean } = {}): Promise<SpecMetafieldOptions> => {
    const query = options.refresh ? '?refresh=true' : '';
    const response = await makeApiCall(`/integrations/${integrationId}/shopify/spec-metafield-options${query}`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data?.error?.message || data?.message || 'Failed to load metafield choices');
    return data.data;
  }

  /** Spec crosswalk CSV (MOT-641): the integration's spec labels beside the store's metafield definitions. */
  downloadSpecCrosswalk = async (integrationId: string): Promise<Blob> => {
    const response = await makeApiCall(`/integrations/${integrationId}/shopify/spec-crosswalk/export`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.error?.message || data?.message || 'Failed to download the crosswalk');
    }
    return response.blob();
  }

  /** Dry run of a completed crosswalk: validates every row and returns the valid ones. Saves nothing. */
  previewSpecCrosswalk = async (integrationId: string, file: File): Promise<SpecCrosswalkPreview> => {
    const form = new FormData();
    form.append('file', file);
    const response = await makeApiCall(`/integrations/${integrationId}/shopify/spec-crosswalk/preview`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: form,
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.error?.message || data?.message || 'Failed to check the file');
    return data.data;
  }

  /** Every product metafield Motzu writes on Shopify (static manifest). */
  getMotzuMetafieldFields = async (): Promise<MotzuMetafieldField[]> => {
    const response = await makeApiCall('/integrations/shopify/metafield-fields', {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data?.error?.message || data?.message || 'Failed to load the field list');
    return data.data || [];
  }

  getShopifyCategoryDefaults = async (): Promise<ShopifyCategoryDefaults> => {
    const response = await makeApiCall('/integrations/shopify/category-defaults', {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || !data?.data) throw new Error(data?.error?.message || data?.message || 'Failed to load Shopify categories');
    return data.data as ShopifyCategoryDefaults;
  }

  /**
   * Shopify's full taxonomy: one level (`parent`, none for Level 1, with the `path` down to it) or a
   * search (`q`). The picker browses with it; the stored value is always the GID.
   */
  browseShopifyTaxonomy = async (params: { parent?: string; q?: string }): Promise<{ items: ShopifyTaxonomyNode[]; path?: ShopifyTaxonomyNode[] }> => {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    else if (params.parent) query.set('parent', params.parent);
    const response = await makeApiCall(`/integrations/shopify/taxonomy?${query.toString()}`, { headers: await this.getHeaders() });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.data) throw new Error(data?.error?.message || data?.message || 'Failed to load Shopify categories');
    return data.data;
  }

  /** Shopify's breadcrumbs for these GIDs; a GID the bundled release does not list is left out. */
  lookupShopifyTaxonomy = async (gids: string[]): Promise<ShopifyTaxonomyNode[]> => {
    if (gids.length === 0) return [];
    const query = new URLSearchParams({ ids: gids.join(',') });
    const response = await makeApiCall(`/integrations/shopify/taxonomy?${query.toString()}`, { headers: await this.getHeaders() });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.data) throw new Error(data?.error?.message || data?.message || 'Failed to load Shopify categories');
    return data.data.items || [];
  }

  /** Flag `shopify_mapping_simple_mode`. `refresh` recomputes on the server and overwrites its 60 s cache, e.g. right after a save. */
  getShopifyCategorySummary = async (integrationId: string, opts: { refresh?: boolean } = {}): Promise<ShopifyCategorySummary> => {
    const params = new URLSearchParams({ integrationId });
    if (opts.refresh) params.set('refresh', 'true');
    const response = await makeApiCall(`/store/shopify-category-summary?${params.toString()}`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.data) throw new Error(data?.error?.message || data?.message || 'Failed to load the category summary');
    return data.data as ShopifyCategorySummary;
  }

  getMappingIndex = async (integrationId: string): Promise<MappingIndex> => {
    const params = new URLSearchParams({ integrationId });
    const response = await makeApiCall(`/store/mapping-index?${params.toString()}`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.data) throw new Error(data?.error?.message || data?.message || 'Failed to load the product index');
    return data.data as MappingIndex;
  }

  private merchantTaxonomyCall = async <T>(path: string, init: RequestInit, failed: string): Promise<T> => {
    const response = await makeApiCall(`/store/merchant-taxonomy${path}`, init);
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.data) throw new Error(data?.error?.message || data?.error || data?.message || failed);
    return data.data as T;
  }

  getMerchantTaxonomy = async (): Promise<{ registry: MerchantTaxonomyRegistry | null; stats: MerchantTaxonomyStats }> =>
    this.merchantTaxonomyCall('', { headers: await this.getHeaders() }, 'Failed to load your taxonomy');

  importMerchantTaxonomy = async (
    file: File,
  ): Promise<{
    registry: MerchantTaxonomyRegistry;
    stats: MerchantTaxonomyStats;
    errors: Array<{ row: number; message: string }>;
    warnings?: Array<{ row: number; message: string }>;
  }> => {
    const form = new FormData();
    form.append('file', file);
    return this.merchantTaxonomyCall(
      `/import?fileName=${encodeURIComponent(file.name)}`,
      { method: 'POST', headers: await this.getHeaders(), body: form },
      'Failed to import the taxonomy',
    );
  }

  /** Parses the file like an import and reports what it would remove and add; nothing is written. */
  previewMerchantTaxonomyImport = async (file: File): Promise<MerchantTaxonomyImportPreview> => {
    const form = new FormData();
    form.append('file', file);
    return this.merchantTaxonomyCall(
      `/import?dryRun=true&fileName=${encodeURIComponent(file.name)}`,
      { method: 'POST', headers: await this.getHeaders(), body: form },
      'Failed to check the taxonomy file',
    );
  }

  updateMerchantTaxonomyEntries = async (
    entries: MerchantTaxonomyPatch[],
  ): Promise<{ registry: MerchantTaxonomyRegistry; stats: MerchantTaxonomyStats }> =>
    this.merchantTaxonomyCall(
      '/entries',
      { method: 'PUT', headers: await this.getHeaders(), body: JSON.stringify({ entries }) },
      'Failed to save the taxonomy',
    );

  resolveMerchantTaxonomy = async (input: { storeSku?: string; productId?: string }): Promise<{ storeSku: string | null; entry: MerchantTaxonomyEntry | null }> =>
    this.merchantTaxonomyCall(
      '/resolve',
      { method: 'POST', headers: await this.getHeaders(), body: JSON.stringify(input) },
      'Failed to resolve the SKU',
    );

  /** Selected products no SKU prefix places and nobody has assigned (owner/admin). */
  listUnmatchedTaxonomyProducts = async (
    opts: { limit?: number; cursor?: string | null; integrationId?: string } = {},
  ): Promise<UnmatchedTaxonomyPage> => {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    if (opts.integrationId) params.set('integrationId', opts.integrationId);
    const query = params.toString();
    return this.merchantTaxonomyCall(
      `/unmatched${query ? `?${query}` : ''}`,
      { headers: await this.getHeaders() },
      'Failed to load products without a match',
    );
  }

  assignTaxonomyProducts = async (assignments: TaxonomyAssignmentInput[]): Promise<{ assigned: number }> =>
    this.merchantTaxonomyCall(
      '/assignments',
      { method: 'PUT', headers: await this.getHeaders(), body: JSON.stringify({ assignments }) },
      'Failed to save the assignments',
    );

  unassignTaxonomyProducts = async (productIds: string[]): Promise<{ removed: number }> =>
    this.merchantTaxonomyCall(
      '/assignments',
      { method: 'DELETE', headers: await this.getHeaders(), body: JSON.stringify({ productIds }) },
      'Failed to remove the assignments',
    );

  /** Flag `taxonomy_classifier_assist`. Writes nothing; the store applies the suggestions it accepts. */
  classifyTaxonomyProducts = async (
    productIds: string[],
    tree: ClassifierTreeNode[],
  ): Promise<{ provider: string; threshold: number; results: TaxonomyClassifierResult[] }> => {
    const response = await makeApiCall('/store/taxonomy-classifier/classify', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ productIds, tree }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.data) throw new Error(data?.error?.message || data?.error || data?.message || 'Failed to suggest categories');
    return data.data;
  }

  getShopifyCustomAppScopes = async (): Promise<ShopifyCustomAppScopes> => {
    const response = await makeApiCall('/integrations/shopify/scopes', {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || !data?.data?.scopeString) throw new Error(data?.error?.message || data?.message || 'Failed to load Shopify scopes');
    return data.data as ShopifyCustomAppScopes;
  }

  getFieldPins = async (integrationId: string): Promise<MotzuFieldPins> => {
    const response = await makeApiCall(`/integrations/${integrationId}/shopify/field-pins`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(apiErrorMessage(data, 'Failed to read pinned fields'));
    }
    return data.data;
  }

  setFieldPin = async (integrationId: string, key: string, pinned: boolean): Promise<MotzuFieldPins> => {
    const response = await makeApiCall(`/integrations/${integrationId}/shopify/field-pins`, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: JSON.stringify({ key, pinned }),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(apiErrorMessage(data, pinned ? 'Failed to pin the field' : 'Failed to unpin the field'));
    }
    return data.data;
  }

  getFieldNamespace = async (integrationId: string): Promise<FieldNamespaceStatus> => {
    const response = await makeApiCall(`/integrations/${integrationId}/shopify/field-namespace`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(apiErrorMessage(data, 'Failed to read the field namespace'));
    }
    return data.data;
  }

  completeFieldNamespace = async (integrationId: string): Promise<FieldNamespaceStatus> => {
    const response = await makeApiCall(`/integrations/${integrationId}/shopify/field-namespace/complete`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(apiErrorMessage(data, 'Failed to complete the migration'));
    }
    return data.data;
  }

  getLayoutHistory = async (integrationId: string): Promise<Array<{ savedAt: string; categoryMap: Record<string, string>; reason: string }>> => {
    const response = await makeApiCall(`/integrations/${integrationId}/layout-history`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    return data.data || [];
  }

  restoreLayout = async (integrationId: string, index: number): Promise<{ categoryMap: Record<string, string> }> => {
    const response = await makeApiCall(`/integrations/${integrationId}/restore-layout`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ index }),
    });
    const data = await response.json();
    return data.data;
  }

  // Storefront Presets — provisioning + theme editor deep links (issue #160 prerequisite).
  // Wires up the endpoints #140 already shipped: declaring the custom.* metafields
  // so the theme editor/Storefront API can see them, and one-click deep links to
  // stage Motzu's product-page blocks.
  provisionMotzuMetafieldDefinitions = async (integrationId: string): Promise<{
    created: number;
    alreadyExists: number;
    updated: number;
    typeConflicts: Array<{ key: string; existingType?: string }>;
    failed: Array<{ key: string; error?: string }>;
    message: string;
  }> => {
    const response = await makeApiCall(`/integrations/${integrationId}/metafield-definitions`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      const message = data?.error?.message || data?.message || 'Failed to provision metafield definitions';
      const error: any = new Error(message);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return { ...data.data, message: data.message };
  }

  getThemeBlockLinks = async (integrationId: string): Promise<{
    blocks: Array<{ handle: string; name: string; description: string; template: string; url: string }>;
    requiresMetafieldDefinitions: boolean;
    requiresOnlineStore2Theme: boolean;
  }> => {
    const response = await makeApiCall(`/integrations/${integrationId}/theme-blocks`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      const message = data?.error?.message || data?.message || 'Failed to load theme block links';
      const error: any = new Error(message);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data.data;
  }

  listShopifyListingHolds = async (integrationId: string): Promise<Array<{
    productId: string;
    productName: string | null;
    productSku: string | null;
    reason: string;
    message: string;
    candidates: Array<{ productId: string; title: string; status?: string | null }>;
  }>> => {
    const response = await makeApiCall(`/integrations/${integrationId}/shopify-listing-holds`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(data?.error?.message || data?.message || 'Failed to load listing matches');
    }
    return data.data || [];
  }

  confirmShopifyListingHold = async (
    integrationId: string,
    productId: string,
    shopifyProductId: string,
  ): Promise<void> => {
    const response = await makeApiCall(
      `/integrations/${integrationId}/shopify-listing-holds/${encodeURIComponent(productId)}/confirm`,
      {
        method: 'POST',
        headers: await this.getHeaders(),
        body: JSON.stringify({ shopifyProductId }),
      },
    );
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(data?.error?.message || data?.message || 'Failed to confirm the listing');
    }
  }

  allowShopifyListingCreate = async (integrationId: string, productId: string): Promise<void> => {
    const response = await makeApiCall(
      `/integrations/${integrationId}/shopify-listing-holds/${encodeURIComponent(productId)}/allow-create`,
      {
        method: 'POST',
        headers: await this.getHeaders(),
      },
    );
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(data?.error?.message || data?.message || 'Failed to allow a new listing');
    }
  }

  // Product Scanning & Matching
  /** Queue a catalog scan. It runs in the background; poll `getScanStatus` for the outcome. */
  scanProducts = async (integrationId: string): Promise<{ status: ShopifyScanJobStatus }> => {
    const response = await makeApiCall(`/integrations/${integrationId}/scan-products`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, 'Scan failed');
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Scan failed');
    }
    return data.data;
  }

  /** State of the scan job; `result` (the preview) is present once it is done. */
  getScanStatus = async (integrationId: string): Promise<ShopifyScanJob> => {
    const response = await makeApiCall(`/integrations/${integrationId}/scan-products`, {
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, 'Could not read the scan status');
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Could not read the scan status');
    }
    return data.data;
  }

  /**
   * Queue a scan and wait for it: POST, then poll the job until it is done. For callers that only
   * want the result (the panel that shows progress polls `getScanStatus` itself).
   */
  scanProductsAndWait = async (
    integrationId: string,
    options: { intervalMs?: number; onProgress?: (job: ShopifyScanJob) => void } = {},
  ): Promise<ProductMatchResult> => {
    await this.scanProducts(integrationId);
    for (;;) {
      const job = await this.getScanStatus(integrationId);
      if (job.status === 'done') {
        if (!job.result) throw new Error('The scan returned no match data.');
        return job.result;
      }
      if (job.status === 'failed') throw new Error(job.error || 'Scan failed');
      if (job.status === 'none') throw new Error('The scan did not start. Try again.');
      options.onProgress?.(job);
      await new Promise((resolve) => setTimeout(resolve, options.intervalMs ?? 2500));
    }
  }

  /** One page (500 rows) of the full match list of the last scan; the scan result is a preview only. */
  getScanMatches = async (
    integrationId: string,
    page: number,
  ): Promise<{ rows: ProductMatchResult['matched']; page: number; pages: number; total: number; pageSize: number }> => {
    const response = await makeApiCall(`/integrations/${integrationId}/scan-matches?page=${page}`, {
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, 'Could not load the scan matches');
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Could not load the scan matches');
    }
    return data.data;
  }

  applyMatches = async (
    integrationId: string,
    matches: Array<{ shopifyProductId: string; canonicalProductId: string }>,
    syncMode?: SyncMode,
    settings?: Partial<Pick<IntegrationSettings, 'syncImages' | 'syncPricing' | 'syncInventory' | 'addMotzuTag'>>,
  ): Promise<ApplyMatchesResult> => {
    const response = await makeApiCall(`/integrations/${integrationId}/apply-matches`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ matches, syncMode, settings }),
    });
    const data = await readJsonResponse(response, 'Could not apply the matches');
    if (!response.ok || data?.success === false || !data?.data) {
      throw new Error(data?.error?.message || data?.message || 'Could not apply the matches');
    }
    return data.data;
  }

  // Lightspeed catalog scan (lightspeed_link_only)
  scanLightspeedCatalog = async (integrationId: string): Promise<LightspeedScanReport> => {
    const response = await makeApiCall(`/integrations/${integrationId}/lightspeed/scan`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, 'Lightspeed scan failed');
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Lightspeed scan failed');
    }
    return data.data;
  }

  // Lightspeed eCom scan (lightspeed_ecom_connector): read-only; the next sync links the matches.
  scanLightspeedEcom = async (integrationId: string): Promise<LightspeedEcomScanReport> => {
    const response = await makeApiCall(`/integrations/${integrationId}/lightspeed-ecom/scan`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, 'Lightspeed eCom scan failed');
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Lightspeed eCom scan failed');
    }
    return data.data;
  }

  getLightspeedEcomScan = async (integrationId: string): Promise<LightspeedEcomScanReport | null> => {
    const response = await makeApiCall(`/integrations/${integrationId}/lightspeed-ecom/scan`, {
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, 'Failed to load Lightspeed eCom scan');
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Failed to load Lightspeed eCom scan');
    }
    return data.data ?? null;
  }

  getLightspeedScan = async (integrationId: string): Promise<LightspeedScanReport | null> => {
    const response = await makeApiCall(`/integrations/${integrationId}/lightspeed/scan`, {
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, 'Failed to load Lightspeed scan');
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Failed to load Lightspeed scan');
    }
    return data.data ?? null;
  }

  getLightspeedScanRows = async (integrationId: string, query: LightspeedScanRowsQuery): Promise<LightspeedScanRowsPage> => {
    const response = await makeApiCall(
      `/integrations/${integrationId}/lightspeed/scan/rows?${lightspeedScanQueryString(query)}`,
      { headers: await this.getHeaders() },
    );
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Failed to load scan rows');
    }
    return data.data;
  }

  /** The list as CSV with the same filters (every row, not one page). */
  downloadLightspeedScanCsv = async (integrationId: string, query: LightspeedScanRowsQuery): Promise<Blob> => {
    const { offset: _offset, limit: _limit, ...filters } = query;
    const response = await makeApiCall(
      `/integrations/${integrationId}/lightspeed/scan/export?${lightspeedScanQueryString(filters)}`,
      { headers: await this.getHeaders() },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.error?.message || data?.message || 'Failed to export scan');
    }
    return response.blob();
  }

  // Lightspeed store configuration (lightspeed_store_config)
  private async lightspeedConfigCall<T>(integrationId: string, path: string, init?: { method: string; body?: unknown }): Promise<T> {
    const response = await makeApiCall(`/integrations/${integrationId}/lightspeed/${path}`, {
      method: init?.method || 'GET',
      headers:
        init?.body !== undefined
          ? { ...(await this.getHeaders()), 'Content-Type': 'application/json' }
          : await this.getHeaders(),
      ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Lightspeed request failed');
    }
    return data.data;
  }

  getLightspeedSyncEstimate = async (integrationId: string, refresh = false): Promise<LightspeedSyncEstimate> => {
    const response = await makeApiCall(
      `/integrations/${integrationId}/lightspeed/sync-estimate${refresh ? '?refresh=true' : ''}`,
      { headers: await this.getHeaders() },
    );
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Could not work out the sync time');
    }
    return data.data as LightspeedSyncEstimate;
  };

  getLightspeedCategories = (integrationId: string) =>
    this.lightspeedConfigCall<LightspeedCategoriesResponse>(integrationId, 'categories');

  getLightspeedCustomFields = (integrationId: string) =>
    this.lightspeedConfigCall<LightspeedCustomFieldsResponse>(integrationId, 'custom-fields');

  saveLightspeedMapping = (
    integrationId: string,
    body: { layout?: Required<Omit<LightspeedLayoutSettings, 'lightspeedCategoryByRegistryEntry'>> & Pick<LightspeedLayoutSettings, 'lightspeedCategoryByRegistryEntry'>; customFields?: Array<{ specLabel: string; customFieldId: string }> },
  ) => this.lightspeedConfigCall<{ layout?: LightspeedLayoutSettings; customFields?: LightspeedCustomFieldMapping[] }>(
    integrationId,
    'mapping',
    { method: 'PUT', body },
  );

  provisionLightspeedCustomFields = (integrationId: string, specLabels: string[]) =>
    this.lightspeedConfigCall<LightspeedProvisionResult>(integrationId, 'custom-fields', {
      method: 'POST',
      body: { specLabels },
    });

  getLightspeedCategoryChanges = (integrationId: string) =>
    this.lightspeedConfigCall<LightspeedCategoryChange[]>(integrationId, 'category-changes');

  syncLightspeedRebateDiscounts = (integrationId: string) =>
    this.lightspeedConfigCall<LightspeedDiscountSyncState>(integrationId, 'rebate-discounts/sync', { method: 'POST' });

  // Lightspeed sales pull (lightspeed_sales_sync). `fullResync` backfills all
  // history instead of pulling only what changed since the stored high-water mark.
  syncLightspeedSales = async (
    integrationId: string,
    options?: { fullResync?: boolean },
  ): Promise<OrderSyncRunResult> => {
    const query = options?.fullResync ? '?fullResync=true' : '';
    const response = await makeApiCall(`/integrations/${integrationId}/lightspeed/sales/sync${query}`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, 'Lightspeed sales sync failed');
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Lightspeed sales sync failed');
    }
    return data.data;
  }

  // Shopify bundle rebate discounts (bundle_shopify_discounts): reconcile the buy-X-get-Y discounts now.
  syncShopifyBundleDiscounts = async (integrationId: string): Promise<ShopifyRebateDiscountSyncState> => {
    const response = await makeApiCall(`/integrations/${integrationId}/shopify/bundle-discounts/sync`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, 'Bundle discount sync failed');
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Bundle discount sync failed');
    }
    return data.data;
  };

  // Shopify rebate discounts (shopify_rebate_discounts): reconcile the automatic discounts now.
  syncShopifyRebateDiscounts = async (integrationId: string): Promise<ShopifyRebateDiscountSyncState> => {
    const response = await makeApiCall(`/integrations/${integrationId}/shopify/rebate-discounts/sync`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, 'Rebate discount sync failed');
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Rebate discount sync failed');
    }
    return data.data;
  }

  // Rebates only (rebate_discount_keep_store_price): Motzu leaves the store's own price alone and carries the rebate as a discount.
  private rebatesOnlyCall = async <T>(integrationId: string, path: string, method: 'GET' | 'POST', fallback: string): Promise<T> => {
    const response = await makeApiCall(`/integrations/${integrationId}/rebates-only${path}`, {
      method,
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, fallback);
    if (!response.ok || !data.success) {
      throw new RebatesOnlyApiError(data?.error?.message || data?.message || fallback, response.status);
    }
    return data.data as T;
  };

  getRebatesOnly = (integrationId: string): Promise<RebatesOnlyState> =>
    this.rebatesOnlyCall<RebatesOnlyState>(integrationId, '', 'GET', 'Could not load the rebate setting');

  previewRebatesOnly = (integrationId: string): Promise<RebatesOnlyPreview> =>
    this.rebatesOnlyCall<RebatesOnlyPreview>(integrationId, '/preview', 'GET', 'Could not preview the change');

  enableRebatesOnly = (integrationId: string): Promise<{ marked: number; runQueued: boolean }> =>
    this.rebatesOnlyCall(integrationId, '/enable', 'POST', 'Could not turn this on');

  disableRebatesOnly = (integrationId: string): Promise<{ enabled: false }> =>
    this.rebatesOnlyCall(integrationId, '/disable', 'POST', 'Could not turn this off');

  // Shopify order pull, used by the sell-through sales report.
  syncShopifyOrders = async (
    integrationId: string,
    options?: { fullResync?: boolean },
  ): Promise<OrderSyncRunResult> => {
    const query = options?.fullResync ? '?full=true' : '';
    const response = await makeApiCall(`/integrations/${integrationId}/sync-orders${query}`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await readJsonResponse(response, 'Order sync failed');
    if (!response.ok || !data.success) {
      const message =
        typeof data?.error === 'string' ? data.error : data?.error?.message || data?.message || 'Order sync failed';
      throw new Error(message);
    }
    return data.data;
  }

  getShopifyDuplicateMappings = async (
    integrationId: string,
  ): Promise<{ groups: ShopifyDuplicateMappingGroup[]; count: number }> => {
    const response = await makeApiCall(`/integrations/${integrationId}/shopify-duplicate-mappings`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Failed to load duplicate mappings');
    }
    return data.data || { groups: [], count: 0 };
  }

  getShopifyOrphanSuspects = async (
    integrationId: string,
    options?: { tag?: string },
  ): Promise<ShopifyOrphanSuspectsResponse> => {
    const params = new URLSearchParams();
    if (options?.tag) params.set('tag', options.tag);
    const qs = params.toString();
    const url = `/integrations/${integrationId}/shopify-orphan-suspects${qs ? `?${qs}` : ''}`;
    const response = await makeApiCall(url, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Failed to scan for orphan listings');
    }
    const d = data.data || {};
    return {
      tag: d.tag ?? 'motzu_synced',
      motzuTaggedProductCount: d.motzuTaggedProductCount ?? 0,
      mappedShopifyIdCount: d.mappedShopifyIdCount ?? 0,
      groups: Array.isArray(d.groups) ? d.groups : [],
    };
  };

  /**
   * Deletes duplicate Shopify listings by numeric product ID: removes Motzu mapping rows (when part of a duplicate group),
   * then deletes each product in Shopify. Orphan IDs (no mapping, not in a duplicate group) delete in Shopify only.
   * Same behavior as the duplicate cleanup UI “Motzu API” flow.
   */
  deleteShopifyDuplicateProductsViaApi = async (
    integrationId: string,
    shopifyProductIds: string[],
  ): Promise<{ deletedMappings: number; shopifyProductsDeleted: number }> => {
    const response = await makeApiCall(
      `/integrations/${integrationId}/shopify-duplicate-mappings/delete-shopify-products`,
      {
        method: 'POST',
        headers: { ...(await this.getHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ shopifyProductIds }),
      },
    );
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Failed to delete Shopify products');
    }
    return data.data || { deletedMappings: 0, shopifyProductsDeleted: 0 };
  };

  removeShopifyDuplicateMappings = async (
    integrationId: string,
    docIds: string[],
    deleteShopifyProductIds?: string[],
  ): Promise<{ deleted: number; removedPlatformProductIds?: string[]; shopifyProductsDeleted?: number }> => {
    const response = await makeApiCall(`/integrations/${integrationId}/shopify-duplicate-mappings/remove`, {
      method: 'POST',
      headers: { ...(await this.getHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        docIds,
        ...(deleteShopifyProductIds?.length ? { deleteShopifyProductIds } : {}),
      }),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Failed to remove mappings');
    }
    return data.data || { deleted: 0 };
  }

  getIngestionBrands = async (): Promise<IngestionBrandInfo[]> => {
    const response = await makeApiCall('/ingestion/brands', {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    return data.data || [];
  }

  getBrandSchedule = async (brandId: string): Promise<BrandScheduleConfig> => {
    const response = await makeApiCall(`/ingestion/brands/${brandId}/schedule`, {
      headers: await this.getHeaders()
    });
    const data = await response.json();
    return data.data;
  }

  updateBrandSchedule = async (brandId: string, payload: UpdateSchedulePayload): Promise<BrandScheduleConfig & { message: string }> => {
    const response = await makeApiCall(`/ingestion/brands/${brandId}/schedule`, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return data.data;
  }

  fetchPricingReports = async (limit = 20, brandId?: string): Promise<PricingReport[]> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (brandId) params.set('brandId', brandId);
    const response = await makeApiCall(`/ingestion/pricing-reports?${params}`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    return data.data || [];
  }

  fetchJobPricingReport = async (jobId: string): Promise<PricingReport | null> => {
    const response = await makeApiCall(`/ingestion/jobs/${jobId}/pricing-report`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    return data.data || null;
  }

  fetchCatalogPriceChanges = async (filters: {
    days?: number;
    brandId?: string;
    region?: string;
    action?: string;
    suspiciousOnly?: boolean;
    includeResolved?: boolean;
    limit?: number;
  } = {}): Promise<{
    data: CatalogPriceChangeRow[];
    count: number;
    summary: {
      reportsScanned: number;
      suspiciousCount: number;
      byBrand: Record<string, number>;
      since: string;
      until: string;
    };
  }> => {
    const params = new URLSearchParams();
    if (filters.days != null) params.set('days', String(filters.days));
    if (filters.brandId) params.set('brandId', filters.brandId);
    if (filters.region) params.set('region', filters.region);
    if (filters.action) params.set('action', filters.action);
    if (filters.suspiciousOnly) params.set('suspiciousOnly', 'true');
    if (filters.includeResolved) params.set('includeResolved', 'true');
    if (filters.limit != null) params.set('limit', String(filters.limit));
    const response = await makeApiCall(`/ingestion/pricing-reports/changes?${params}`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to load catalog price changes');
    return {
      data: data.data || [],
      count: data.count || 0,
      summary: data.summary || {
        reportsScanned: 0,
        suspiciousCount: 0,
        byBrand: {},
        since: '',
        until: '',
      },
    };
  }

  resolveCatalogPriceChanges = async (
    items: Array<{ reportId: string; changeIndex: number }>
  ): Promise<{ updatedCount: number }> => {
    const response = await makeApiCall('/ingestion/pricing-reports/changes/resolve', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ items }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to resolve price changes');
    return { updatedCount: data.updatedCount || 0 };
  }

  decidePriceDeviationReview = async (body: {
    reportId: string;
    changeIndex: number;
    decision: 'approved' | 'rejected';
  }): Promise<{ decision: 'approved' | 'rejected'; productId: string }> => {
    const response = await makeApiCall('/ingestion/pricing-reports/changes/price-deviation-review', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to decide the held price read');
    return data;
  }

  downloadCatalogPriceChangesCsv = async (filters: {
    days?: number;
    brandId?: string;
    region?: string;
    action?: string;
    suspiciousOnly?: boolean;
    includeResolved?: boolean;
    limit?: number;
  } = {}): Promise<void> => {
    const params = new URLSearchParams();
    if (filters.days != null) params.set('days', String(filters.days));
    if (filters.brandId) params.set('brandId', filters.brandId);
    if (filters.region) params.set('region', filters.region);
    if (filters.action) params.set('action', filters.action);
    if (filters.suspiciousOnly) params.set('suspiciousOnly', 'true');
    if (filters.includeResolved) params.set('includeResolved', 'true');
    if (filters.limit != null) params.set('limit', String(filters.limit));
    const response = await makeApiCall(`/ingestion/pricing-reports/changes.csv?${params}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to export catalog price changes');
    }
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `catalog-price-changes-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(downloadUrl);
  }

  fetchStalePricingProducts = async (filters: {
    days?: number;
    brandId?: string;
    limit?: number;
  } = {}): Promise<StalePricingReport> => {
    const params = new URLSearchParams();
    if (filters.days != null) params.set('days', String(filters.days));
    if (filters.brandId) params.set('brandId', filters.brandId);
    if (filters.limit != null) params.set('limit', String(filters.limit));
    const response = await makeApiCall(`/ingestion/pricing-coverage/stale?${params}`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to load pricing freshness report');
    return {
      data: data.data || [],
      count: data.count || 0,
      totalStale: data.totalStale || 0,
      truncated: Boolean(data.truncated),
      summary: data.summary || {
        staleAfterDays: filters.days ?? 7,
        scanned: 0,
        stale: 0,
        neverChecked: 0,
        missingUrl: 0,
        repeatedFailures: 0,
        byBrand: {},
      },
    };
  }

  downloadStalePricingCsv = async (filters: {
    days?: number;
    brandId?: string;
    limit?: number;
  } = {}): Promise<void> => {
    const params = new URLSearchParams();
    if (filters.days != null) params.set('days', String(filters.days));
    if (filters.brandId) params.set('brandId', filters.brandId);
    if (filters.limit != null) params.set('limit', String(filters.limit));
    const response = await makeApiCall(`/ingestion/pricing-coverage/stale.csv?${params}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to export pricing freshness report');
    }
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `pricing-freshness-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(downloadUrl);
  }

  fetchAnnouncementCandidates = async (status?: string, limit = 30): Promise<any[]> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (status) params.set('status', status);
    const response = await makeApiCall(`/ingestion/announcements?${params}`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    return data.data || [];
  }

  runAnnouncementMonitor = async (): Promise<any> => {
    const response = await makeApiCall('/ingestion/announcements/run', {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to run announcement monitor');
    return data.data;
  }

  dismissAnnouncementCandidate = async (id: string): Promise<void> => {
    const response = await makeApiCall(`/ingestion/announcements/${id}/dismiss`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to dismiss candidate');
    }
  }

  /** Stage a press announcement as a product with lifecycle `announced` (flag `preorder_lifecycle`, MOT-143). */
  stageAnnouncementCandidate = async (
    id: string,
    body: { sku: string; model: string; name?: string; productType?: 'camera' | 'lens' | 'accessory'; category?: string; releaseDate?: string | null; preorderOpensAt?: string | null },
  ): Promise<{ stagedProductId: string; lifecycle: { stage: string } }> => {
    const response = await makeApiCall(`/ingestion/announcements/${id}/stage`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to stage announcement');
    return data.data;
  }

  scrapeAnnouncementCandidate = async (id: string, productUrl?: string): Promise<string> => {
    const response = await makeApiCall(`/ingestion/announcements/${id}/scrape`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(productUrl ? { productUrl } : {}),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to scrape candidate');
    return data.data?.jobId;
  }

  enqueueProductEnrichment = async (payload: {
    brandId: string;
    sourceUrl: string;
    types: string[];
    productId?: string;
    stagedProductId?: string;
  }): Promise<string[]> => {
    const response = await makeApiCall('/ingestion/enrichment', {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to enqueue enrichment');
    return data.data?.taskIds || [];
  }

  provisionMetafieldDefinitions = async (
    integrationId: string,
  ): Promise<{ summary: MetafieldProvisionSummary; message: string }> => {
    const response = await makeApiCall(`/integrations/${integrationId}/metafield-definitions`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(apiErrorMessage(data, 'Failed to provision metafield definitions'));
    }
    return {
      summary: data.data,
      message: data.message || 'Metafield definitions provisioned.',
    };
  };

  getThemeBlocks = async (integrationId: string): Promise<ThemeBlocksResponse> => {
    const response = await makeApiCall(`/integrations/${integrationId}/theme-blocks`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(apiErrorMessage(data, 'Failed to load theme editor links'));
    }
    return data.data;
  };

  installCustomAppThemeSections = async (
    integrationId: string,
    body: { force?: boolean } = {},
  ): Promise<CustomAppThemeSectionResult> => {
    const response = await makeApiCall(`/integrations/${integrationId}/theme-sections/install`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.success === false) {
      throw integrationApiError(data, 'The unpublished copy could not be created.');
    }
    return data.data;
  };

  updateCustomAppThemeSections = async (
    integrationId: string,
    body: { themeId: string },
  ): Promise<CustomAppThemeSectionResult> => {
    const response = await makeApiCall(`/integrations/${integrationId}/theme-sections/update`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.success === false) {
      throw integrationApiError(data, 'The sections could not be updated.');
    }
    return data.data;
  };

  listCustomAppThemes = async (integrationId: string): Promise<CustomAppThemeSummary[]> => {
    const response = await makeApiCall(`/integrations/${integrationId}/theme-sections/themes`, {
      headers: await this.getHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.success === false) {
      throw integrationApiError(data, 'The unpublished themes could not be loaded.');
    }
    return data.data?.themes || [];
  };

  downloadCustomAppThemeSections = async (integrationId: string): Promise<void> => {
    const response = await makeApiCall(`/integrations/${integrationId}/theme-sections/pack`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw integrationApiError(data, 'Failed to download the sections pack');
    }
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = 'motzu-theme-sections.zip';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  };

  getStorefrontPresets = async (): Promise<StorefrontPresetSummary[]> => {
    const response = await makeApiCall('/integrations/storefront-presets', {
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(apiErrorMessage(data, 'Failed to load storefront presets'));
    }
    return data.data || [];
  };

  downloadWooComparePlugin = async (integrationId: string): Promise<void> => {
    const response = await makeApiCall(`/integrations/${integrationId}/woocommerce/compare-plugin`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(apiErrorMessage(data, 'Failed to download the compare plugin'));
    }
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = 'motzu-compare.zip';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  };

  downloadThemeZip = async (integrationId: string, payload: ThemeZipRequest): Promise<void> => {
    const response = await makeApiCall(`/integrations/${integrationId}/theme-zip`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(apiErrorMessage(data, 'Failed to build theme zip'));
    }
    const blob = await response.blob();
    const header = response.headers.get('Content-Disposition') || '';
    const match = /filename="([^"]+)"/i.exec(header);
    const filename = match?.[1] || `${payload.presetId}-theme.zip`;
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  };
}

/** `GET /integrations/:id/lightspeed/sync-estimate` (flag `lightspeed_sync_estimates`). Mirrors `SyncEstimate` in the API. */
export type LightspeedSyncEstimateMode = 'link_only' | 'enrich' | 'override';
export interface LightspeedSyncEstimate {
  /** False when the account would not report its rate limit and Lightspeed's defaults were used. */
  throttleMeasured: boolean;
  /** ISO time the limit was last read from the account; null or absent when it never was. */
  throttleMeasuredAt?: string | null;
  /**
   * What this call's read of the limit found: `read`, `not_reported` (the account answered with no limit),
   * `failed` (the read went wrong; worth retrying), or null when the cached reading was used.
   */
  throttleReadStatus?: 'read' | 'not_reported' | 'failed' | null;
  /** Why the read failed, only with `throttleReadStatus: 'failed'`. */
  throttleReadError?: string | null;
  throttle: { bucketSize: number; dripRate: number; burst: number | null };
  selected: number;
  linked: number;
  writeCostAssumed: boolean;
  modes: Record<LightspeedSyncEstimateMode, { mode: LightspeedSyncEstimateMode; totalUnits: number; seconds: number; secondsMax: number; atLeast: boolean; phases: Array<{ id: string; count: number; units: number }> }>;
  photoImpact: { current: { photos: number; seconds: number }; full: { photos: number; seconds: number }; extraSeconds: number };
}

export const integrationApiService = new IntegrationApiService();
